import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  Phone,
  Mail,
  MapPin,
  XCircle,
  AlertCircle,
  File,
  Trash2,
  Check,
  MoveLeft,
  Share,
  Clock3,
  ChartNoAxesColumn,
  Shield,
  Award,
  CirclePlay,
  Video,
  CirclePause,
  CircleX,
  CircleCheck,
  TriangleAlert,
  Flag,
  Footprints,
  ChevronDown,
  ChevronUp,
  LockKeyhole,
  Download,
  X,
  ChartPie,
  ShieldBan,
  Eye,
  XIcon,
  Play,
  ExternalLink,
  ArrowLeft,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import host from '../../global';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import PDFGenerator from '../Reports/PrintReport';
import CloseIcon from '@mui/icons-material/Close';
import { EmailOutlined } from '@mui/icons-material';
import MCQReport from '../Reports/MCQResult';
import BehavioralResult from '../Reports/BehavioralResult';
import CodingTestReport from '../Reports/CodingResult';
import TypingTestReport from '../Reports/TypingTestResult';
import { buildCameraSlides, cameraTileMeta, fmtClockTime } from './SecondaryCameraMonitoringCard';
import { VideoPlayerModal } from '../Reports/VideoPlayerModal';
import AnalysisReportModal from '../common/AnalysisReportModal';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ConfirmationDialog from './ConfirmationDialog';
import ReportComment from './ReportComment';
import ProctoringSummaryCard, { verdictTitle } from './ProctoringSummaryCard';
import { useDispatch, useSelector } from 'react-redux';
import {
  addInterviewCommentThunk,
  getInterviewCommentsThunk,
} from '../../store/thunks/InterviewThunk';
import { resetInterviewCommentsState } from '../../store/slices/interviewCommentsSlice';
import { useTheme } from '../../hooks/useTheme';
// import {
//   addInterviewCommentThunk,
//   getInterviewCommentsThunk,
// } from '../../store/thunks/InterviewThunk';
// import { resetInterviewCommentsState } from '../../store/slices/interviewCommentsSlice';
// import { useTheme } from '../../hooks/useTheme';
import { SkipBack, SkipForward } from 'lucide-react';
import { updateCandidateStatusService } from '../../Services/interviewManagement.service';
import {
  addCandidateToRejectedList,
  addCandidateToShortListedList,
  updateCandidateStatus,
} from '../../store/slices/InterviewManagementSlice';
import LimitExceededModal from '../shared_components/LimitExceededModal';
import AddRemarkModal from './Remarks';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const STATUSES = {
  COMPLETE: 'Complete',
  PASSED: 'Passed',
  INCOMPLETE: 'Incomplete',
  IN_PROGRESS: 'In Progress',
};
const theme = createTheme({
  components: {
    MuiChartsAxis: {
      styleOverrides: {
        line: { stroke: '#D9D9D9 !important' },
        tick: { stroke: '#D9D9D9 !important' },
        tickLabel: { fill: '#D9D9D9 !important' },
        label: { fill: '#D9D9D9 !important' },
      },
    },
  },
});

const ProgressIndicator = React.memo(({ stages }) => {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between w-full mt-8 space-y-4 sm:space-y-0">
      {stages.map((stage, index) => {
        const isCompleted = [STATUSES.COMPLETE, STATUSES.PASSED].includes(
          stage.status,
        );
        const isIncomplete = stage.status === STATUSES.INCOMPLETE;
        const isInProgress = stage.status === STATUSES.IN_PROGRESS;
        const isLast = index === stages.length - 1;

        return (
          <React.Fragment key={`${stage.name}-${index}`}>
            <div
              className="flex flex-col items-center"
              aria-label={`Stage: ${stage.name}, Status: ${stage.status}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                  isCompleted
                    ? 'bg-[#4F46E5] border-[#4F46E5]'
                    : isIncomplete
                      ? 'dark:bg-gray-700 bg-gray-200 dark:border-gray-500 border-gray-100'
                      : 'bg-yellow-500 border-yellow-500'
                }`}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5 text-white" />
                ) : isIncomplete ? (
                  <div className="h-3 w-3 rounded-full bg-[#D1D5DB]" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-white" />
                )}
              </div>
              <span className="text-xs dark:text-[#A8A8A8] text-textSecondary mt-2 text-center">
                {stage.name}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mx-2 sm:mx-4 ${
                  isCompleted ? 'bg-[#4F46E5]' : 'bg-[#D1D5DB]'
                } hidden sm:block`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

const parseResumeContent = (text) => {
  const nameMatch = text.match(/^[^\r\n]+/);
  const emailMatch = text.match(/Email:([^|]+)/);
  const phoneMatch = text.match(/Mobile:\+?(\d+)/);
  const projectMatches = text.match(
    /PROJECTS([\s\S]*?)POSITIONOFRESPONSIBILITY/,
  );
  const educationMatches = text.match(/EDUCATION([\s\S]*?)SKILLS/);
  const skillsMatch = text.match(/SKILLS([\s\S]*?)CERTIfiCATIONS/);
  const locationMatch = text.match(/Location:([^\r\n]+)/);

  return {
    name: nameMatch ? nameMatch[0].trim() : null,
    email: emailMatch ? emailMatch[1].trim() : null,
    phone: phoneMatch ? `+${phoneMatch[1].trim()}` : null,
    location: locationMatch ? locationMatch[1].trim() : null,
    projects: projectMatches
      ? projectMatches[1]
          .split('•')
          .map((p) => p.trim())
          .filter(Boolean)
      : [],
    education: educationMatches
      ? educationMatches[1]
          .split('\r\n')
          .map((e) => e.trim())
          .filter(Boolean)
      : [],
    skills: skillsMatch
      ? skillsMatch[1]
          .split('\r\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
  };
};

const getPathColor2 = (recommendation) => {
  switch (recommendation?.toLowerCase()) {
    case 'rejected':
      return '#DC0000';
    case 'needs improvement':
      return '#FFB200';
    default:
      return '#34B53A';
  }
};

const getPathColor = (val) => {
  switch (true) {
    case val >= 60:
      return '#34B53A';
    case val >= 30 && val < 60:
      return '#FFB200';
    case val >= 0 && val < 30:
      return '#DC0000';
    default:
      return 'gray';
  }
};

const SkeletonEvaluationReport = () => (
  <div className="min-h-screen px-2 sm:px-2 md:px-4 lg:px-8 mb-8">
    <div className="flex flex-col py-2 gap-4">
      {/* Row 1: Candidate Info (75%) + Intro Video (25%) */}
      <div className="flex gap-2">
        {/* Candidate Info Card */}
        <div className="w-[75%] bg-bgPrimary dark:bg-white/5 rounded-2xl px-3 py-1 animate-pulse">
          <div className="flex gap-2 items-center py-2">
            {/* Avatar */}
            <div className="flex-shrink-0 ml-4">
              <div className="w-40 h-40 rounded-full dark:bg-gray-700 bg-gray-200" />
            </div>
            <div className="w-full px-6 py-4 space-y-4">
              {/* Name + Download button row */}
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="h-7 w-48 dark:bg-gray-700 bg-gray-200 rounded" />
                  <div className="h-4 w-32 dark:bg-gray-700 bg-gray-200 rounded" />
                </div>
                <div className="h-9 w-36 dark:bg-gray-700 bg-gray-200 rounded-full" />
              </div>
              {/* Details row 1 */}
              <div className="flex gap-6">
                <div className="h-4 w-24 dark:bg-gray-700 bg-gray-200 rounded" />
                <div className="h-4 w-24 dark:bg-gray-700 bg-gray-200 rounded" />
                <div className="h-4 w-28 dark:bg-gray-700 bg-gray-200 rounded" />
                <div className="h-4 w-20 dark:bg-gray-700 bg-gray-200 rounded" />
              </div>
              {/* Details row 2 */}
              <div className="flex gap-6">
                <div className="h-4 w-44 dark:bg-gray-700 bg-gray-200 rounded" />
                <div className="h-4 w-32 dark:bg-gray-700 bg-gray-200 rounded" />
              </div>
              {/* Buttons row */}
              <div className="flex gap-4">
                <div className="h-9 w-40 dark:bg-gray-700 bg-gray-200 rounded-full" />
                <div className="h-9 w-40 dark:bg-gray-700 bg-gray-200 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Intro Video Card */}
        <div className="w-[25%] bg-bgPrimary dark:bg-white/5 rounded-2xl py-1 px-4 animate-pulse">
          <div className="h-4 w-36 dark:bg-gray-700 bg-gray-200 rounded mb-3" />
          <div className="w-full h-40 dark:bg-gray-700 bg-gray-200 rounded-lg" />
        </div>
      </div>

      {/* Row 2: Interview Outcome (75%) + Proctoring (25%) */}
      <div className="flex h-68 gap-2">
        {/* Interview Outcome Card */}
        <div className="w-[75%] bg-bgPrimary dark:bg-white/5 rounded-2xl px-4 py-3 animate-pulse space-y-4">
          <div className="h-4 w-52 dark:bg-gray-700 bg-gray-200 rounded" />
          {/* Recommendation badge */}
          <div className="h-10 w-11/12 dark:bg-gray-700 bg-gray-200 rounded-xl" />
          {/* Bullet points */}
          <div className="space-y-3 mt-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full dark:bg-gray-700 bg-gray-200 flex-shrink-0 mt-0.5" />
                <div
                  className={`h-4 dark:bg-gray-700 bg-gray-200 rounded ${i === 0 ? 'w-3/4' : i === 1 ? 'w-2/3' : 'w-1/2'}`}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-row gap-4">
                <div className="h-9 w-20 dark:bg-gray-700 bg-gray-200 rounded-full" />
                <div className="h-9 w-20 dark:bg-gray-700 bg-gray-200 rounded-full" />
              </div>
        </div>

        {/* Right: Proctoring + Add Remarks stacked */}
        <div className="w-[25%] space-y-2">
          {/* Proctoring Card */}
          <div className="bg-bgPrimary dark:bg-white/5 rounded-2xl px-4 py-4 animate-pulse space-y-4">
            <div className="flex justify-between items-center">
              <div className="h-4 w-36 dark:bg-gray-700 bg-gray-200 rounded" />
              <div className="h-8 w-24 dark:bg-gray-700 bg-gray-200 rounded-full" />
            </div>
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-3 py-2">
                <div className="w-12 h-12 rounded-full dark:bg-gray-700 bg-gray-200 flex-shrink-0" />
                <div className="space-y-1">
                  <div className="h-3 w-20 dark:bg-gray-700 bg-gray-200 rounded" />
                  <div className="h-3 w-16 dark:bg-gray-700 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
          {/* Add Remarks Card */}
          <div className="bg-bgPrimary dark:bg-white/5 rounded-2xl px-4 py-3 animate-pulse">
            <div className="flex justify-between items-center">
              <div className="h-4 w-28 dark:bg-gray-700 bg-gray-200 rounded" />
              <div className="h-8 w-20 dark:bg-gray-700 bg-gray-200 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Individual Assessment Performance */}
      <div className="mt-4 bg-bgPrimary dark:bg-white/5 rounded-2xl overflow-hidden animate-pulse">
        {/* Title */}
        <div className="px-5 pt-4 mb-4">
          <div className="h-4 w-56 dark:bg-gray-700 bg-gray-200 rounded" />
        </div>
        {/* Tabs */}
        <div className="border-b dark:border-gray-700 border-gray-200 px-0">
          <div className="flex gap-0">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="px-6 py-4">
                <div className="h-4 w-28 dark:bg-gray-700 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
        {/* Tab content area */}
        <div className="p-6 space-y-6">
          {/* Sub-tab pills */}
          <div className="flex gap-2">
            <div className="h-9 w-28 dark:bg-gray-700 bg-gray-200 rounded-full" />
            <div className="h-9 w-36 dark:bg-gray-700 bg-gray-200 rounded-full" />
          </div>
          {/* Score circle + skills */}
          <div className="flex items-stretch gap-6">
            <div className="w-[25%] flex items-center justify-center">
              <div className="w-40 h-40 rounded-full dark:bg-gray-700 bg-gray-200" />
            </div>
            <div className="w-[75%] space-y-4 justify-center flex flex-col">
              <div className="h-4 w-24 dark:bg-gray-700 bg-gray-200 rounded" />
              <div className="flex gap-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-8 w-24 dark:bg-gray-700 bg-gray-200 rounded-full"
                  />
                ))}
              </div>
              <div className="h-4 w-36 dark:bg-gray-700 bg-gray-200 rounded mt-4" />
              <div className="flex gap-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-8 w-24 dark:bg-gray-700 bg-gray-200 rounded-full"
                  />
                ))}
              </div>
            </div>
          </div>
          {/* Score cards row */}
          <div className="flex gap-4 mt-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="flex flex-col items-center w-[150px] border dark:border-gray-700 border-gray-200 rounded-2xl p-4 gap-3"
              >
                <div className="h-3 w-20 dark:bg-gray-700 bg-gray-200 rounded" />
                <div className="w-20 h-20 rounded-full dark:bg-gray-700 bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  </div>
);

const getScoreColorGradient2 = (recommendation) => {
  switch (recommendation?.toLowerCase()) {
    case 'rejected':
      return 'rgba(220, 0, 0, 0.2)';
    case 'needs improvement':
      return 'rgba(255, 178, 0, 0.2)';
    default:
      return 'rgba(52, 181, 58, 0.2)';
  }
};

const getScoreColorGradient = (score) => {
  switch (true) {
    case score >= 60:
      return 'rgba(0,255,0,0.2)';
    case score >= 30 && score < 60:
      return 'rgba(255, 255, 0, 0.2)';
    case score >= 0 && score < 30:
      return 'rgba(255, 0, 0, 0.2)';
    default:
      return 'text-[#CCCCCCE5]';
  }
};

// const SkillBadge = ({ skill, status }) => {
//   return (
//     <div className="flex items-center gap-1.5">
//       <span className="dark:text-gray-300 text-textSecondary">{skill}</span>
//       {status === 'yes' ? (
//         <CheckCircle className="w-4 h-4 text-green-500" />
//       ) : (
//         <XCircle className="w-4 h-4 text-red-500" />
//       )}
//     </div>
//   );
// };

const SkillBadge = ({ skill, status }) => {
  return (
    <div className="flex items-center gap-1.5">
      <span className="dark:text-gray-300 text-textSecondary">{skill}</span>
      {status === 'yes' ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <XCircle className="w-4 h-4 text-red-500" />
      )}
    </div>
  );
};

// Shared shape for a Violation-Detections-style tile — used by the existing
// per-violation-type tiles and the secondary-camera tiles alike.
const ReportTile = ({ label, count, seeDetails, onClick, countTone, subline }) => (
  <div
    className="dark:glass-effect bg-white rounded-xl cursor-pointer transition-colors dark:hover:bg-gray-700/20 hover:bg-gray-50 border dark:border-0 border-gray-200"
    onClick={onClick}
  >
    <div className="flex justify-between items-center mb-2 bg-gray-50/10 px-4 py-3 rounded-t-xl">
      <h4 className="dark:text-[#C9A3FF] text-textSecondary text-sm font-normal">{label}</h4>
      {seeDetails && (
        <span className="text-xs italic dark:text-[#C9A3FF] text-purple-600 underline">
          See Details
        </span>
      )}
    </div>
    <div className="px-4 py-2">
      <span className={`font-medium text-xl ${countTone || 'text-[#FF3030]'}`}>
        {count.toString().padStart(2, '0')}
      </span>
      {subline && (
        <p className="dark:text-[#A8A8A8] text-textSecondary text-xs mt-1">
          {subline}
        </p>
      )}
    </div>
  </div>
);

// Same shape/sizing as ReportTile so the grid slot doesn't reflow once the
// real tile replaces it — the secondary-camera tile is fetched on-demand
// (only when the modal opens), so it'd otherwise pop in after the others.
const ReportTileSkeleton = () => (
  <div className="dark:glass-effect bg-white rounded-xl border dark:border-0 border-gray-200 animate-pulse">
    <div className="flex justify-between items-center mb-2 bg-gray-50/10 px-4 py-3 rounded-t-xl">
      <div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-700" />
    </div>
    <div className="px-4 py-2">
      <div className="h-6 w-10 rounded bg-gray-200 dark:bg-gray-700" />
    </div>
  </div>
);

// const EvaluationReportOrgAdmin = () => {
const EvaluationReportOrgAdmin = ({ publicToken = null, publicSession = null }) => {
  // Public (share-token) mode: PublicReport.jsx renders this same component after
  // its OTP gate, so the two reports stay pixel-identical. In this mode data comes
  // from the single /public-report endpoint (a safe subset — no PII) and every
  // admin-only action is hidden via `can`.
  const isPublic = Boolean(publicToken);
  const can = {
    share: !isPublic,
    remarks: !isPublic,
    status: !isPublic,
    resume: !isPublic,
    analysis: !isPublic,
    pdf: !isPublic,
    back: !isPublic,
    translate: !isPublic,
    delete: !isPublic,
  };
  const selectableStyles = `
    /* Make every piece of text selectable */
    .selectable-report * {
      user-select: text !important;
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
    }
  `;

  const profile_photos_host = process.env.REACT_APP_PROFILE_PHOTOS_HOST;
  // Follows the viewer's theme on both pages. The public report opens dark
  // (PublicReport.jsx flips the theme once on mount) but has a toggle, and it
  // shares this hook - forcing 'dark' here left light-mode text on dark,
  // translucent dialog surfaces once a viewer switched to light.
  const { theme: savedTheme } = useTheme();
  const isDarkMode = savedTheme;
  const { interview_id: routeInterviewId } = useParams();
  const [publicInterviewId, setPublicInterviewId] = useState(null);
  // In public mode the route param is the share token, not the interview id — the
  // real id arrives in the /public-report payload.
  const interview_id = routeInterviewId || publicInterviewId;
  const org_id = useSelector((state) => state.userSlice.org_id);
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useLocation();
  const {
    combined_status = null,
    job_position = null,
    page = null,
    search_query = null,
    dateRange = null,
    candidate: candidateFromReduxForAPI = null,
  } = state || {};
  const portal = location.pathname.startsWith('/hr') ? 'hr' : 'org-admin';
  const accessToken = sessionStorage.getItem('accessToken');
  const [candidateData, setCandidateData] = useState({});
  const [rawResumeData, setResumeData] = useState('');
  const [stages, setStages] = useState([]);
  const [completedStages, setCompleteStages] = useState(0);
  const [scores, setScores] = useState({});
  const [assessmentSequence, setAssessmentSequence] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewResumeModal, setIsViewResumeModal] = useState(false);
  const [evaluationData, setEvaluationData] = useState([]);
  // English translation is fetched lazily on first toggle and cached on evaluationData.
  const [showEnglish, setShowEnglish] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const handleToggleEnglish = async () => {
    if (showEnglish) {
      setShowEnglish(false);
      return;
    }
    if (evaluationData?.questions_en) {
      setShowEnglish(true);
      return;
    }
    try {
      setIsTranslating(true);
      const res = await axios.post(
        `${host}/translate-report/${interview_id}`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` }, params: { org_id } }
      );
      setEvaluationData((prev) => ({ ...prev, ...res.data }));
      setShowEnglish(true);
    } catch (e) {
      toast.error('Could not translate the report. Showing the original language.');
    } finally {
      setIsTranslating(false);
    }
  };
  const [isResumeLoading, setIsResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const [showEvaluationDetails, setshowEvaluationDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('SmartEvaluation');
  const [showTypingReport, setShowTypingReport] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const evaluationDetailsRef = useRef(null);
  const [selectedDomain, setSelectedDomain] = useState('All');
  const [isDomainOpen, setIsDomainOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showReasonModel, setShowReasonModel] = useState(false);
  const [openModal, setOpenModal] = useState({
    mcq: false,
    coding: false,
    typing: false,
  });
  const [featureFlags, setFeatureFlags] = useState({
    video_included: false,
    storing_enabled: false,
    interactivity_enabled: false,
    live_avatar_enabled: false,
    assembly_tts_enabled: false,
    interview_v1_animation: false,
    tab_switch_screenshot: false,
    secondary_camera_enabled: false,
    report_sharing: false,
  });
  const [limitModal, setLimitModal] = useState({
    isOpen: false,
    featureKey: null,
    errorCode: null,
  });
  const [showMCQReport, setShowMCQReport] = useState(false);
  const [showCodingReport, setShowCodingReport] = useState(false);
  const [showBehavioralReport, setShowBehavioralReport] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [nativeVideoFailed, setNativeVideoFailed] = useState(false);
  const videoRequestRef = useRef('');
  // Remembers URLs that have already failed a native <video> playback
  // attempt, so re-opening the same clip goes straight to the iframe
  // instead of paying for another doomed attempt first.
  const failedVideoUrlsRef = useRef(new Set());
  const [videoLibraryOpen, setVideoLibraryOpen] = useState(false);
  const [liveAvatarRecordingOpen, setLiveAvatarRecordingOpen] = useState(false);
  const questionRefs = useRef([]);
  const questionsContainerRef = useRef(null);
  const [expandedViolation, setExpandedViolation] = useState(null);
  const [violationScreenshots, setViolationScreenshots] = useState({});
  const [showProctoringDetails, setShowProctoringDetails] = useState(false);
  const [selectedViolationScreenshots, setSelectedViolationScreenshots] =
    useState(null);
  const [showProctoringReport, setShowProctoringReport] = useState(false);
  const [proctoringModalOpen, setProctoringModalOpen] = useState(false);
  const [proctoringModalTab, setProctoringModalTab] = useState('interview');
  const [codingViolationData, setCodingViolationData] = useState(null);
  const [codingViolationLoading, setCodingViolationLoading] = useState(false);
  const [secondaryCameraData, setSecondaryCameraData] = useState(null);
  const [secondaryCameraLoading, setSecondaryCameraLoading] = useState(false);
  const [showCodingProctoringDetails, setShowCodingProctoringDetails] = useState(false);
  const [selectedCodingViolationScreenshots, setSelectedCodingViolationScreenshots] = useState(null);
  const [isAddRemarkModalOpen, setIsAddRemarkModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [comment, setComment] = useState('');
  const [videoRef, setVideoRef] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const videoElementRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [analysisReportData, setAnalysisReportData] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [additionalInfoPage, setAdditionalInfoPage] = useState(0);
  const [openActions, setOpenActions] = useState(false);
  // Simple global reference to currently playing audio (stores { element, pauseCallback })
  const activeMainTab = useSelector(
    (state) => state.candidateManagementSlice.activeTab,
  );
  const currentlyPlayingAudio = useRef(null);

  // Get Interview Comments Here
  const interviewComments = useSelector(
    (state) => state.interviewCommentsSlice.comments,
  );

  const { loading: commentsLoading, error: commentsError } = useSelector(
    (state) => state.interviewCommentsSlice,
  );

  const isAdminLoggedIn = useSelector(
    (state) => state.interviewCommentsSlice.isAdminLoggedIn,
  );
  const dispatch = useDispatch();
  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Always try a plain <video> tag first, which is where our custom
  // controls (seek bar, skip, fullscreen) live — it plays instantly, no
  // upfront download or format check. Only if that actually fails to play
  // (onError, via handleNativeVideoError below) do we fall back to an
  // iframe, which plays unfinalized WebM (no Cues seek index — MediaRecorder
  // in every browser except Safari never finalizes the container) the same
  // way navigating a tab directly to the file would — at the cost of the
  // browser's native player chrome instead of our custom controls, since a
  // cross-origin iframe can't be scripted.
  const openVideoModal = useCallback((url, duration = null) => {
    setSelectedVideoUrl(url);
    setVideoOpen(true);
    setVideoDuration(duration);
    setIsPlaying(false);
    setCurrentTime(0);
    videoRequestRef.current = url;
    setNativeVideoFailed(failedVideoUrlsRef.current.has(url));
  }, []);

  const handleNativeVideoError = useCallback(() => {
    if (nativeVideoFailed) return; // already falling back — avoid retry loops
    failedVideoUrlsRef.current.add(selectedVideoUrl);
    setNativeVideoFailed(true);
  }, [nativeVideoFailed, selectedVideoUrl]);

  const handleAddComment = () => {
    console.log('Add Comment Clicked');
    if (interview_id) {
      dispatch(
        addInterviewCommentThunk({
          interviewId: interview_id,
          commentContent: comment,
          isAdmin: isAdminLoggedIn,
        }),
      );
      // toast.success("Comment added successfully");
    }
    setComment('');
  };

  const handleViewAnalysisReport = () => {
    if (candidateData && candidateData.score !== undefined) {
      const reportData = {
        score: candidateData.score,
        experience_match: candidateData.experience_match || [],
        location_match: candidateData.location_match || {},
        education_match: candidateData.education_match || {},
        tech_tool_match: candidateData.tech_tool_match || [],
        red_flags: candidateData.red_flags || [],
        // Strengths: candidateData.report?.Strengths || [],
        // Weaknesses: candidateData.report?.Weaknesses || [],
        // Suggestions: candidateData.report?.Suggestions || [],
        resume_s3_url: candidateData.resume_s3_url,
        name: candidateData.name,
      };
      setAnalysisReportData(reportData);
      setIsAnalysisModalOpen(true);
    } else {
      toast.info('No analysis report available for this candidate.');
    }
  };

  let parsedReport = {};

  try {
    parsedReport =
      typeof candidateData?.report === 'string'
        ? JSON.parse(candidateData.report)
        : candidateData?.report || {};
  } catch (e) {
    parsedReport = {};
  }

  const strengths = parsedReport?.Strengths || [];
  const weaknesses = parsedReport?.Weaknesses || [];
  const suggestions = parsedReport?.Suggestions || [];

  useEffect(() => {
    if (interview_id) {
      dispatch(getInterviewCommentsThunk(interview_id));
    }

    return () => {
      // Cleanup if necessary
      console.log('This is Return Called In UseEffect');
      dispatch(resetInterviewCommentsState());
    };
  }, []);

  const actionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setOpenActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // console.log({ interviewComments });

  const canCurrentUserDelete = () => {
    if (!currentUserId || !candidateData?.admin_id) return false;
    if (currentUserId === candidateData.admin_id) return true;

    if (portal === 'org-admin') return true;

    return false;
  };

  const hasViolationData = useMemo(() => {
    if (
      evaluationData?.proctoring_report?.verified_violation_counts &&
      typeof evaluationData.proctoring_report.verified_violation_counts ===
        'object' &&
      Object.keys(evaluationData.proctoring_report.verified_violation_counts)
        .length > 0
    ) {
      return true;
    }

    // Fallback to old structure
    return (
      evaluationData?.violation_counts &&
      typeof evaluationData.violation_counts === 'object' &&
      Object.keys(evaluationData.violation_counts).length > 0
    );
  }, [evaluationData]);

  const formatViolationType = (violationType) => {
    const violationMap = {
      fullscreen_exit: 'Fullscreen Exit',
      gaze: 'Candidate Looking Away',
      hands_not_visible: 'Both Hands not Visible',
      multiple_faces: 'Multiple Faces Detected',
      multi_monitor_detected: 'Multi Monitor Detected',
      tab_switch: 'Tab Switch',
      prohibited_item: 'Prohibited Item',
      no_face: 'No Face',
      secondary_camera_disconnect: 'Secondary Camera Disconnected',
      secondary_camera_interview: 'Secondary Camera',
      secondary_camera_coding: 'Secondary Camera',
      body_not_visible: 'Posture',
      windows_key_detected: 'Restricted Keyboard Actions',
    };
    return violationMap[violationType] || violationType;
  };

  // Together with COUNT_ONLY_VIOLATIONS below, the ten checks the proctoring
  // card accounts for, in display order. These six can carry captured
  // screenshots, so the card offers Review on them.
  const SCREENSHOT_VIOLATIONS = [
    'no_face',
    'multiple_faces',
    'hands_not_visible',
    'gaze',
    'prohibited_item',
    'body_not_visible',
  ];

  // Counted with no image behind them: flagged like the rest, but never offered
  // a Review link that would lead nowhere.
  const COUNT_ONLY_VIOLATIONS = [
    'fullscreen_exit',
    'tab_switch',
    'multi_monitor_detected',
    'windows_key_detected',
  ];

  // The proctoring analysis labels answers "Q3" and "Q3.F1"; the video player
  // addresses them by array position. 1-based label -> 0-based index.
  const clipForAnswerLabel = (label) => {
    const m = /^Q(\d+)(?:\.F(\d+))?$/.exec(label || '');
    if (!m) return null;
    return {
      index: Number(m[1]) - 1,
      followUpIndex: m[2] ? Number(m[2]) - 1 : null,
    };
  };

  // One row per analysed answer (proctoring_report.answers), joined to its
  // question text, stored answer and recording. The report plays recordings
  // inline, so a row carries its own url.
  const proctoringAnswers = useMemo(() => {
    const stored = evaluationData?.proctoring_report?.answers;
    return (Array.isArray(stored) ? stored : []).map((a) => {
      const clip = clipForAnswerLabel(a.label);
      let fu;
      if (clip && clip.followUpIndex !== null) {
        fu = evaluationData?.follow_ups?.[clip.index]?.[clip.followUpIndex];
      }
      return {
        label: a.label,
        isFollowup: !!a.is_followup,
        status: a.status,
        reason: a.reason,
        question: clip
          ? (fu ? fu.question : evaluationData?.questions?.[clip.index])
          : undefined,
        transcript: clip
          ? (fu ? fu.answer : evaluationData?.answers?.[clip.index])
          : undefined,
        url: clip
          ? (fu ? fu.user_video_response : evaluationData?.video_urls?.[clip.index])
          : undefined,
      };
    });
  }, [evaluationData]);

  const getViolationScreenshots = (violationType) => {
    if (evaluationData?.proctoring_report?.verified_violations_only) {
      return evaluationData.proctoring_report.verified_violations_only
        .filter((v) => v.violation_type === violationType && v.screenshot_url)
        .map((v) => ({
          url: v.screenshot_url,
          timestamp: v.timestamp,
          confidence: v.cheating_likelihood,
          verified: v.verified,
          explanation: v.explanation,
        }));
    }

    // Fallback to old structure
    if (evaluationData?.violation_logs) {
      return evaluationData.violation_logs
        .filter(
          (log) => log.violation_type === violationType && log.screenshot_url,
        )
        .map((log) => ({
          url: log.screenshot_url,
          timestamp: log.timestamp,
          confidence: log.confidence_percent,
        }));
    }

    return [];
  };

  const CODING_VIOLATION_TYPE_LABELS = {
    fullscreen_exit: 'Fullscreen Exit',
    tab_switch: 'Tab Switch',
    multi_monitor_detected: 'Multiple Monitors Detected',
    multiple_faces: 'Multiple Faces Detected',
    prohibited_item: 'Prohibited Item Detected',
    secondary_camera_coding: 'Secondary Camera',
  };

  const formatCodingViolationType = (type) =>
    CODING_VIOLATION_TYPE_LABELS[type] || type;

  const getCodingViolationScreenshots = (violationType) => {
    if (!codingViolationData?.violation_logs) return [];
    return codingViolationData.violation_logs
      .filter((log) => log.violation_type === violationType && log.screenshot_url)
      .map((log) => ({ url: log.screenshot_url, timestamp: log.timestamp }));
  };

  const codingViolationHasScreenshots = (violationType) =>
    getCodingViolationScreenshots(violationType).length > 0;

  const fetchCodingViolationData = async () => {
    if (codingViolationData !== null) return;
    if (isPublic) {
      setCodingViolationData({});
      return;
    }
    setCodingViolationLoading(true);
    try {
      const response = await axios.get(`${host}/api/get_codesubmissions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { interview_id },
      });
      setCodingViolationData(response.data);
    } catch {
      setCodingViolationData({});
    } finally {
      setCodingViolationLoading(false);
    }
  };

  const fetchSecondaryCameraData = async () => {
    if (secondaryCameraData !== null) return;
    // Shared reports get the captures inside the /public-report payload
    // (reports_service._public_report_extras), so there is nothing to fetch.
    // Settle without entering the loading state, or the row stays a placeholder.
    if (isPublic) {
      setSecondaryCameraData(evaluationData?.secondary_camera || { rounds: [] });
      return;
    }
    setSecondaryCameraLoading(true);
    try {
      const response = await axios.get(
        `${host}/secondary-camera/screenshots/${interview_id}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSecondaryCameraData(response.data);
    } catch {
      setSecondaryCameraData({ rounds: [] });
    } finally {
      setSecondaryCameraLoading(false);
    }
  };

  const getProctoringStatusColor = (status) => {
    if (!status) return 'text-[#A8A8A8]';

    const statusLower = status.toLowerCase();

    if (statusLower.includes('no verified violations')) {
      return 'text-[#34B53A]';
    } else if (
      statusLower.includes('minor distractions observed') ||
      statusLower.includes('possible misconduct indicators')
    ) {
      return 'text-[#FFB200]';
    } else if (statusLower.includes('major proctoring violations detected')) {
      return 'text-[#FF3030]';
    }
    return 'text-[#FF3030]';
  };

  const ProctoringScreenshotsView = ({
    violationType,
    screenshots,
    onBack,
  }) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const goToPrevious = () => {
      setSelectedImageIndex((prev) =>
        prev > 0 ? prev - 1 : screenshots.length - 1,
      );
    };

    const goToNext = () => {
      setSelectedImageIndex((prev) =>
        prev < screenshots.length - 1 ? prev + 1 : 0,
      );
    };

    return (
      <div className="px-6">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-[#C9A3FF] hover:text-purple-400"
          >
            <MoveLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
          </button>
          <h3 className="dark:text-[#C9A3FF] text-textPrimary text-md font-normal ml-4">
            {formatViolationType(violationType)} - Screenshots
          </h3>
        </div>

        {screenshots && screenshots.length > 0 ? (
          <div className="flex flex-col gap-4">
            {/* Navigation row + Main Image */}
            <div className="flex items-center gap-4">
              {/* Previous Button */}
              <button
                onClick={goToPrevious}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 flex items-center justify-center text-white transition-all"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Main Image */}
              <div className="relative flex-1">
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ height: '420px' }}
                >
                  {screenshots[selectedImageIndex].url ? (
                    <img
                      src={screenshots[selectedImageIndex].url}
                      alt={`${violationType} violation ${selectedImageIndex + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    // No photo for this slide — a gap in the secondary-camera
                    // timeline, standing in for the missing frame.
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-red-500/10">
                      <AlertCircle className="w-8 h-8 text-red-400" />
                      <span className="text-red-400 text-sm font-medium">
                        Disconnected · {screenshots[selectedImageIndex].gapMinutes} min
                      </span>
                    </div>
                  )}
                </div>
                {/* Image counter */}
                <div className="absolute top-3 right-3 bg-black bg-opacity-70 px-3 py-1.5 rounded text-xs text-white">
                  {selectedImageIndex + 1} / {screenshots.length}
                  {screenshots[selectedImageIndex].confidence && (
                    <span className="ml-2">
                      • {screenshots[selectedImageIndex].confidence}%
                    </span>
                  )}
                  {screenshots[selectedImageIndex].timestamp && (
                    <span className="ml-2">
                      · {fmtClockTime(screenshots[selectedImageIndex].timestamp)}
                    </span>
                  )}
                </div>
                {screenshots[selectedImageIndex].deviceChanged && (
                  <div className="absolute bottom-3 left-3 bg-black bg-opacity-70 px-3 py-1.5 rounded text-xs text-amber-400">
                    🔄 new device
                  </div>
                )}
              </div>

              {/* Next Button */}
              <button
                onClick={goToNext}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 flex items-center justify-center text-white transition-all"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            {/* Thumbnail carousel */}
            <div className="flex justify-center overflow-x-auto py-2 px-4">
              <div className="flex gap-2">
                {screenshots.map((screenshot, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`cursor-pointer transition-all flex-shrink-0 ${
                      selectedImageIndex === index
                        ? 'ring-2 ring-[#C9A3FF] opacity-100 scale-105'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{ width: '80px' }}
                  >
                    <div className="aspect-video bg-gray-900 rounded overflow-hidden border border-gray-500">
                      {screenshot.url ? (
                        <img
                          src={screenshot.url}
                          alt={`${violationType} thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-red-500/10">
                          <AlertCircle className="w-4 h-4 text-red-400" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="dark:text-[#A8A8A8] text-textSecondary text-sm">
            No screenshots available for this violation type.
          </div>
        )}
      </div>
    );
  };

  const handleViewResume = async () => {
    if (candidateData.resume_s3_url) {
      try {
        const response = await axios.get(
          `${host}/download-resume/${interview_id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            responseType: 'blob',
          },
        );

        const contentDisposition = response.headers['content-disposition'];
        const contentType =
          response.headers['content-type'] || 'application/octet-stream';

        let filename = 'resume';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        } else {
          const candidateName = (candidateData.name || 'candidate')
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_');
          filename = `${candidateName}_resume`;
        }

        let extension = 'pdf';
        if (contentType.includes('wordprocessingml')) extension = 'docx';
        else if (contentType.includes('msword')) extension = 'doc';
        else if (contentType.includes('pdf')) extension = 'pdf';

        if (!filename.endsWith(`.${extension}`)) {
          filename = `${filename}.${extension}`;
        }

        const blob = new Blob([response.data], { type: contentType });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          link.style.display = 'none';
          link.setAttribute('target', '_self');

          document.body.appendChild(link);

          setTimeout(() => {
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          }, 100);
        }

        toast.success('Resume downloaded successfully');
      } catch (error) {
        console.error('Download error:', error);

        if (error.response?.status === 404) {
          const errorMessage =
            error.response?.data?.detail || 'Resume not found';
          toast.error(errorMessage);
        } else if (error.response?.status === 401) {
          toast.error('Unauthorized access. Please login again.');
        } else if (error.response?.status === 400) {
          const errorMessage =
            error.response?.data?.detail || 'Invalid request';
          toast.error(errorMessage);
        } else if (error.response?.status === 500) {
          toast.error('Server error. Please try again later.');
        } else {
          toast.error('Download failed. Please try again.');
        }
      }
    } else {
      setIsViewResumeModal(true);
    }
  };

  const scrollToTop = () => {
    if (questionsContainerRef.current) {
      questionsContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleModal = (modal) => {
    setOpenModal((prev) => ({ ...prev, [modal]: !prev[modal] }));
  };

  const structuredResumeData = useMemo(
    () => parseResumeContent(rawResumeData),
    [rawResumeData],
  );

  const domains = useMemo(() => {
    const uniqueDomains = [...new Set(evaluationData?.domain_or_skill || [])];
    return ['All', ...uniqueDomains.filter(Boolean)];
  }, [evaluationData]);

  const filteredQuestions = useMemo(() => {
    if (selectedDomain === 'All') {
      return evaluationData?.questions?.map((_, index) => index) || [];
    }
    return (
      evaluationData?.questions
        ?.map((_, index) => index)
        .filter(
          (index) => evaluationData?.domain_or_skill[index] === selectedDomain,
        ) || []
    );
  }, [selectedDomain, evaluationData]);

  const domainAverageScores = useMemo(() => {
    if (!evaluationData?.domain_or_skill || !evaluationData?.scores) return {};

    const domainScores = {};
    const domainCounts = {};

    evaluationData.domain_or_skill.forEach((domain, index) => {
      if (domain) {
        // Ensure domain is not null or undefined
        const score = Number(evaluationData.scores[index]);
        if (!isNaN(score)) {
          // Ensure score is a valid number
          domainScores[domain] = (domainScores[domain] || 0) + score;
          domainCounts[domain] = (domainCounts[domain] || 0) + 1;
        }
      }
    });

    return Object.keys(domainScores).reduce((acc, domain) => {
      acc[domain] = Number(
        (domainScores[domain] / domainCounts[domain]).toFixed(1),
      );
      return acc;
    }, {});
  }, [evaluationData]);

  useEffect(() => {
    if (showEvaluationDetails && evaluationDetailsRef.current) {
      evaluationDetailsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setActiveTab('SmartEvaluation');
    }
  }, [showEvaluationDetails]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      // No need for manual auth checks - axios interceptor handles all 401 errors
      if (!isPublic && !routeInterviewId) {
        setIsLoading(false);
        return;
      }

      try {
        let candidateResponse, scoresResponse, evaluationResponse, featureFlagsResponse;

        if (isPublic) {
          // One call replaces check_test_progress + get_testscores + evaluation-v2
          // + get_media_feature_flags. Shape the pieces so the code below is shared.
          const { data } = await axios.get(
            `${host}/public-report/${publicToken}`,
            { params: publicSession || {} },
          );
          if (!isMounted) return;
          const ci = data.candidate_info || {};
          candidateResponse = {
            data: {
              ...ci,
              candidate_photo_url: data.candidate_photo_url || '',
              invited_date: ci.invited_date || data.startDateTime || '',
            },
          };
          scoresResponse = { data: data.assessment_scores || {} };
          evaluationResponse = { data };
          featureFlagsResponse = { data: data.feature_flags || {} };
          if (data.interview_id) setPublicInterviewId(data.interview_id);
        } else {
          [
            candidateResponse,
            scoresResponse,
            evaluationResponse,
            featureFlagsResponse,
          ] = await Promise.all([
            axios.get(`${host}/check_test_progress/${routeInterviewId}`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            }),
            axios.get(`${host}/get_testscores`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { interview_id: routeInterviewId },
            }),
            axios.get(`${host}/evaluation-v2`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { interview_id: routeInterviewId, org_id },
            }),
            axios.get(`${host}/get_media_feature_flags`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { org_id },
            }),
          ]);
        }

        if (!isMounted) return;

        const candidate = candidateResponse.data;
        setCandidateData(candidate);
        setResumeData(candidate?.resume_content || '');

        // Get current user ID from JWT token payload
        if (!isPublic && accessToken) {
          try {
            const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
            setCurrentUserId(tokenPayload.sub);
          } catch (error) {
            console.error('Failed to decode token:', error);
          }
        }

        const sequence = candidate.assessment_sequence || [
          'mcq',
          'coding',
          'typing',
          'behavioral',
          'interview',
        ];
        setAssessmentSequence(sequence);

        const testTypeMap = {
          mcq: { name: 'MCQ', status: candidate.mcq_status },
          coding: { name: 'Coding', status: candidate.coding_status },
          typing: {
            name: 'Typing Test',
            status: candidate.typing_status,
          },
          behavioral: {
            name: 'Behavioural Assessment',
            status: candidate.behavioral_status,
          },
          interview: {
            name: 'AI Interview',
            status: candidate.interview_status,
          },
        };

        const dynamicStages = [
          { name: 'Invited for Test', status: STATUSES.COMPLETE },
        ];
        sequence.forEach((testType) => {
          if (testTypeMap[testType]) {
            dynamicStages.push({
              name: testTypeMap[testType].name,
              status: testTypeMap[testType].status || STATUSES.INCOMPLETE,
            });
          }
        });
        dynamicStages.push({
          name: 'Test Complete',
          status: STATUSES.INCOMPLETE,
        });

        const validStages = dynamicStages.filter(
          (stage) =>
            stage.status !== 'NA' &&
            stage.status !== undefined &&
            stage.status !== null,
        );

        const allOthersComplete = validStages
          .filter((stage) => stage.name !== 'Test Complete')
          .every((stage) =>
            [STATUSES.COMPLETE, STATUSES.PASSED].includes(stage.status),
          );

        if (allOthersComplete) {
          const testCompleteStage = validStages.find(
            (stage) => stage.name === 'Test Complete',
          );
          if (testCompleteStage) testCompleteStage.status = STATUSES.COMPLETE;
        }

        setStages(validStages);
        setCompleteStages(
          validStages.reduce(
            (acc, stage) =>
              acc + ([STATUSES.INCOMPLETE].includes(stage.status) ? 0 : 1),
            0,
          ),
        );

        const formattedScores = Object.keys(scoresResponse.data)
          .filter((key) => {
            const value = scoresResponse.data[key];
            return value !== 'NA' && value !== null && value !== undefined;
          })
          .reduce((acc, key) => {
            const value = scoresResponse.data[key];
            acc[key] = parseFloat(value).toFixed(1);
            return acc;
          }, {});
        setScores(formattedScores);

        setEvaluationData({
          ...evaluationResponse?.data,
          video_urls: (evaluationResponse?.data?.video_urls || []).filter(
            (url) => url && typeof url === 'string',
          ),
        });
        if (featureFlagsResponse?.data) {
          setFeatureFlags(featureFlagsResponse.data);
        }
        setIsLoading(false);
      } catch (error) {
        if (!isMounted) return;
        setIsLoading(false);
        handleError(error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [accessToken, routeInterviewId, isPublic, publicToken]);

  useEffect(() => {
    if (!candidateData || Object.keys(candidateData).length === 0) return;

    const assessmentStatuses = [
      {
        type: 'interview',
        status: candidateData.interview_status,
        isComplete:
          candidateData.interview_status === STATUSES.COMPLETE ||
          candidateData.interview_status === STATUSES.PASSED,
        action: () => {
          setshowEvaluationDetails(true);
          setShowTypingReport(false);
          setShowMCQReport(false);
          setShowCodingReport(false);
          setShowBehavioralReport(false);
          setActiveTab('SmartEvaluation');
        },
      },
      {
        type: 'typing',
        status: candidateData.typing_status,
        isComplete:
          candidateData.typing_status === STATUSES.COMPLETE ||
          candidateData.typing_status === STATUSES.PASSED,
        action: () => {
          setShowTypingReport(true);
          setshowEvaluationDetails(false);
          setShowMCQReport(false);
          setShowCodingReport(false);
          setShowBehavioralReport(false);
        },
      },
      {
        type: 'mcq',
        status: candidateData.mcq_status,
        isComplete:
          candidateData.mcq_status === STATUSES.COMPLETE ||
          candidateData.mcq_status === STATUSES.PASSED,
        action: () => {
          setShowMCQReport(true);
          setShowTypingReport(false);
          setshowEvaluationDetails(false);
          setShowCodingReport(false);
          setShowBehavioralReport(false);
        },
      },
      {
        type: 'coding',
        status: candidateData.coding_status,
        isComplete:
          candidateData.coding_status === STATUSES.COMPLETE ||
          candidateData.coding_status === STATUSES.PASSED,
        action: () => {
          setShowCodingReport(true);
          setShowMCQReport(false);
          setShowTypingReport(false);
          setshowEvaluationDetails(false);
          setShowBehavioralReport(false);
        },
      },
      {
        type: 'behavioral',
        status: candidateData.behavioral_status,
        isComplete:
          candidateData.behavioral_status === STATUSES.COMPLETE ||
          candidateData.behavioral_status === STATUSES.PASSED,
        action: () => {
          setShowBehavioralReport(true);
          setShowCodingReport(false);
          setShowMCQReport(false);
          setShowTypingReport(false);
          setshowEvaluationDetails(false);
        },
      },
    ];

    const completedAssessments = assessmentStatuses.filter(
      (assessment) =>
        assessment.status &&
        assessment.status !== 'NA' &&
        assessment.isComplete,
    );

    if (completedAssessments.length === 0) {
      setshowEvaluationDetails(false);
      setShowTypingReport(false);
      setShowMCQReport(false);
      setShowCodingReport(false);
      setShowBehavioralReport(false);
      return;
    }

    // Priority order: interview > typing > mcq > coding > behavioral
    const priorityOrder = [
      'interview',
      'typing',
      'mcq',
      'coding',
      'behavioral',
    ];

    const highestPriority = priorityOrder
      .map((type) => completedAssessments.find((a) => a.type === type))
      .find((a) => a !== undefined);

    if (highestPriority) {
      highestPriority.action();
    }
  }, [candidateData]);

  const handleError = (error) => {
    if (error.response) {
      const {
        status,
        data: { detail },
      } = error.response;
      if (status === 401) {
        sessionStorage.removeItem('accessToken');
        toast.error(`Unauthorized: ${detail}`);
      } else if (status === 404) {
        toast.warning(`Not Found: ${detail}`);
      } else {
        toast.error(`Error: ${detail}`);
      }
    } else {
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  const getInitials = (name) =>
    name
      ? name
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase())
          .join('')
      : '';

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${host}/deletecandidate_data`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { interview_id },
      });
      toast.success(response.data.message);
      const basePath = portal === 'hr' ? '/hr' : '/org-admin';
      setTimeout(() => {
        navigate(`${basePath}/candidatereports`, {
          state: {
            page: page ?? 1,
            job_position: job_position ?? [],
            combined_status: combined_status ?? 'Status',
            search_query: search_query ?? '',
            dateRange: dateRange ?? [null, null],
          },
        });
      }, 2000);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const getScoreColor = (recommendation) => {
    switch (recommendation?.toLowerCase()) {
      case 'rejected':
        return 'text-[#DC0000]';
      case 'needs improvement':
        return 'text-[#FFB82E]';
      default:
        return 'text-[#1BAA0E]';
    }
  };

  const honestyMonitor = useMemo(
    () => [
      { label: 'Device Used', value: 'Desktop', status: true },
      { label: 'Webcam Enabled?', value: '', status: true },
      { label: 'Full screen mode always active?', value: '', status: true },
      { label: 'Mouse always in Test window?', value: '', status: true },
      {
        label: 'Filled out only once from IP Address?',
        value: '',
        status: false,
      },
    ],
    [],
  );

  const authenticityReport = [
    'Authenticity \nConfidence Score',
    'Plagiarism \nDetection Score',
  ];

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setRecipientEmail('');
  };

  const handleShareReport = async () => {
    if (!recipientEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSharing(true);
    try {
      const response = await axios.post(
        `${host}/share-report`,
        {
          interview_id,
          recipient_email: recipientEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('accessToken') || ''}`,
          },
        },
      );

      if (response.status === 200) {
        toast.success('Report shared successfully!');
        handleShareDialogClose();
      } else {
        toast.error('Failed to share report');
      }
    } catch (error) {
      console.error('Share Error:', error);
      toast.error(
        `Error sharing report: ${error.response?.data?.detail || error.message}`,
      );
    } finally {
      setSharing(false);
    }
  };

  if (isLoading) return <SkeletonEvaluationReport />;

  const ScoreDisplay = ({ score_value }) => {
    const scoreEntries = Object.entries(score_value || {});
    if (scoreEntries.length === 0) {
      return (
        <div className="mx-auto mt-5 w-full max-w-7xl">
          <div className="flex items-cente">
            <p className="text-[#A8A8A8] text-sm ml-6">
              No score data available
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="mt-5 w-full px-14">
        <div className="flex flex-wrap gap-8 mt-5">
          {scoreEntries.map(([key, value]) => (
            <div
              key={key}
              className="flex flex-col items-center w-[150px] justify-between border border-[#52525280] dark:glass-effect bg-bgPrimary rounded-2xl p-4"
            >
              <div className="dark:text-[#A8A8A8] text-textPrimary text-sm mb-2 text-center">
                {key}
              </div>
              <div className="h-[100px] w-[100px]">
                <CircularProgressbar
                  value={value}
                  text={`${value}%`}
                  circleRatio={0.75}
                  styles={buildStyles({
                    rotation: 0.625,
                    strokeLinecap: 'butt',
                    trailColor: getScoreColorGradient(value),
                    pathColor: getPathColor(value),
                    textColor: `${isDarkMode === 'dark' ? '#FFFFFF' : '#525252'}`,
                    textSize: '20px',
                    text: {
                      dominantBaseline: 'middle',
                      textAnchor: 'middle',
                    },
                  })}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const QuestionAnswerCard = ({
    questionNo,
    question,
    answer,
    questionEn,
    answerEn,
    showEnglish,
    followUpsEn,
    domain,
    score,
    remark,
    audioUrl,
    videoUrl,
    videoDuration,
    follow_ups,
    openVideoModal,
    currentlyPlayingAudio,
  }) => {
    const [audioState, setAudioState] = useState({ playing: false });
    const audioRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showFollowUps, setShowFollowUps] = useState(false);
    const [followUpAudioStates, setFollowUpAudioStates] = useState({});
    const followUpAudioRefs = useRef({});
    const CHAR_LIMIT = 250;

    const truncateText = (text, limit) => {
      if (!text || text.length <= limit) return text;
      return text.substring(0, limit);
    };

    const toggleReadMore = () => {
      setIsExpanded(!isExpanded);
    };

    // pause currently playing audio and update its state
    const pauseCurrentlyPlaying = () => {
      if (currentlyPlayingAudio.current) {
        const { element, pauseCallback } = currentlyPlayingAudio.current;
        if (element && !element.paused) {
          element.pause();
          pauseCallback?.();
        }
        currentlyPlayingAudio.current = null;
      }
    };

    // Add 'ended' event listener to main audio
    useEffect(() => {
      const mainAudio = audioRef.current;
      if (mainAudio) {
        const handleEnded = () => {
          setAudioState({ playing: false });
          if (currentlyPlayingAudio.current?.element === mainAudio) {
            currentlyPlayingAudio.current = null;
          }
        };
        mainAudio.addEventListener('ended', handleEnded);
        return () => mainAudio.removeEventListener('ended', handleEnded);
      }
    }, []); // Empty - only set up once

    const toggleAudio = () => {
      if (audioState.playing) {
        audioRef.current?.pause();
        setAudioState({ playing: false });
        currentlyPlayingAudio.current = null;
      } else {
        pauseCurrentlyPlaying(); // Pause any other playing audio

        const playPromise = audioRef.current?.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setAudioState({ playing: true });
              currentlyPlayingAudio.current = {
                element: audioRef.current,
                pauseCallback: () => setAudioState({ playing: false }),
              };
            })
            .catch((error) => {
              console.error('Audio playback failed:', error);
              setAudioState({ playing: false });
            });
        }
      }
    };

    const toggleFollowUpAudio = async (followUpIndex) => {
      const audioKey = `q${questionNo}-fu${followUpIndex}`;
      const audioElement = followUpAudioRefs.current[audioKey];
      const isPlaying = followUpAudioStates[audioKey]?.playing;

      if (!audioElement) {
        return;
      }

      if (isPlaying) {
        audioElement.pause();
        setFollowUpAudioStates((prev) => ({
          ...prev,
          [audioKey]: { playing: false },
        }));
        currentlyPlayingAudio.current = null;
      } else {
        pauseCurrentlyPlaying(); // Pause any other playing audio

        try {
          // Load the audio if not already loaded
          if (audioElement.readyState < 2) {
            audioElement.load();
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                resolve();
              };
              const onError = () => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                reject(new Error('Failed to load audio'));
              };
              audioElement.addEventListener('canplay', onCanPlay);
              audioElement.addEventListener('error', onError);
            });
          }

          await audioElement.play();
          setFollowUpAudioStates((prev) => ({
            ...prev,
            [audioKey]: { playing: true },
          }));
          currentlyPlayingAudio.current = {
            element: audioElement,
            pauseCallback: () =>
              setFollowUpAudioStates((prev) => ({
                ...prev,
                [audioKey]: { playing: false },
              })),
          };
        } catch (error) {
          setFollowUpAudioStates((prev) => ({
            ...prev,
            [audioKey]: { playing: false },
          }));
        }
      }
    };

    const displayQuestion = showEnglish && questionEn ? questionEn : question;
    const displayAnswer = showEnglish && answerEn ? answerEn : answer;

    return (
      <div
        className="rounded-lg mb-6 pt-6 mt-5 border border-[#52525280]"
        style={{
          background:
            'linear-gradient(233.57deg, rgba(217, 217, 217, 0.05) -5.01%, rgba(115, 108, 207, 0.01) 100%)',
        }}
      >
        <div className="px-6">
          <div className="flex">
            <p className="dark:text-[#A8A8A8] text-textSecondary text-sm">{`${questionNo}.`}</p>
            <p className="dark:text-[#A8A8A8] text-textSecondary ml-2 text-sm">
              {displayQuestion}
            </p>
          </div>
          <div className="mt-2">
            <p className="dark:text-[#A8A8A8] text-textSecondary font-semibold text-sm">
              Response:
            </p>
            <p className="dark:text-[#A8A8A8] text-textSecondary text-sm mt-1">
              {isExpanded || !displayAnswer || displayAnswer.length <= CHAR_LIMIT
                ? displayAnswer
                : `${truncateText(displayAnswer, CHAR_LIMIT)}...`}
              {displayAnswer?.length > CHAR_LIMIT && !isExpanded && (
                <span
                  onClick={toggleReadMore}
                  className="text-[#C9A3FF] underline cursor-pointer hover:text-purple-400 ml-2"
                >
                  Read More
                </span>
              )}
              {isExpanded && displayAnswer?.length > CHAR_LIMIT && (
                <span
                  onClick={toggleReadMore}
                  className="text-[#C9A3FF] underline cursor-pointer hover:text-purple-400 ml-2"
                >
                  Read Less
                </span>
              )}
            </p>
          </div>

          {/* Follow-ups Section */}
          {follow_ups && follow_ups.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowFollowUps(!showFollowUps)}
                className="flex items-center gap-2 text-[#C9A3FF] hover:text-purple-400 transition-colors text-sm font-medium"
              >
                <span
                  className={`transform transition-transform duration-200 ${
                    showFollowUps ? 'rotate-90' : ''
                  }`}
                >
                  ▶
                </span>
                {follow_ups.length} Follow-up{follow_ups.length > 1 ? 's' : ''}
              </button>
              {showFollowUps && (
                <div className="mt-3 ml-6 border-l-2 border-[#C9A3FF]/30 pl-4 space-y-4">
                  {follow_ups.map((fu, idx) => {
                    const audioKey = `q${questionNo}-fu${idx}`;
                    return (
                      <div
                        key={idx}
                        className="text-sm border border-[#52525280] rounded-lg p-4"
                      >
                        <p className="text-[#C9A3FF] font-medium">
                          Follow-up {idx + 1}:{' '}
                          {showEnglish && followUpsEn?.[idx]?.question_en
                            ? followUpsEn[idx].question_en
                            : fu.question}
                        </p>
                        <p className="dark:text-[#A8A8A8] text-textSecondary mt-2">
                          <span className="font-semibold">Response:</span>{' '}
                          {(showEnglish && followUpsEn?.[idx]?.answer_en
                            ? followUpsEn[idx].answer_en
                            : fu.answer) || 'No response'}
                        </p>

                        {/* Audio and Video for Follow-ups */}
                        <div className="flex items-center mt-3 space-x-4">
                          {fu.user_response && (
                            <>
                              <button
                                className="flex items-center"
                                onClick={() => toggleFollowUpAudio(idx)}
                              >
                                {!followUpAudioStates[audioKey]?.playing ? (
                                  <CirclePlay
                                    className="w-7 h-7 rounded-full p-1 text-white"
                                    style={{
                                      background:
                                        'linear-gradient(267.39deg, rgba(255, 96, 182, 1) 0.32%, rgba(41, 96, 253, 1) 100.25%)',
                                    }}
                                  />
                                ) : (
                                  <CirclePause
                                    className="w-7 h-7 rounded-full p-1 text-white"
                                    style={{
                                      background:
                                        'linear-gradient(267.39deg, rgba(255, 96, 182, 1) 0.32%, rgba(41, 96, 253, 1) 100.25%)',
                                    }}
                                  />
                                )}
                                <p className="dark:text-[#C9A3FF] text-purple-600 text-sm ml-2">
                                  Audio Available
                                </p>
                              </button>
                              <audio
                                ref={(el) =>
                                  (followUpAudioRefs.current[audioKey] = el)
                                }
                                onEnded={() =>
                                  setFollowUpAudioStates((prev) => ({
                                    ...prev,
                                    [audioKey]: { playing: false },
                                  }))
                                }
                              >
                                <source
                                  src={fu.user_response}
                                  type="audio/mpeg"
                                />
                                Your browser does not support the audio element.
                              </audio>
                            </>
                          )}
                          {fu.user_video_response && (
                            <button
                              className="flex items-center"
                              onClick={() => {
                                openVideoModal(fu.user_video_response);
                              }}
                            >
                              <Video
                                className="w-7 h-7 rounded-full p-1 text-white"
                                style={{
                                  background:
                                    'linear-gradient(267.39deg, rgba(255, 96, 182, 1) 0.32%, rgba(41, 96, 253, 1) 100.25%)',
                                }}
                              />
                              <p className="dark:text-[#C9A3FF] text-purple-600 text-sm ml-2">
                                Video Available
                              </p>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex mt-2">
            <p className="dark:text-[#C9A3FF] text-purple-600 font-semibold text-sm">
              Remark:
            </p>
            <p className="dark:text-[#C9A3FF] text-purple-600 text-sm ml-2">
              {remark}
            </p>
          </div>
        </div>
        <div className="flex mt-5">
          <div
            className="flex items-center justify-center py-1 w-32 rounded-tr-3xl"
            style={{
              background:
                'linear-gradient(267.39deg, rgba(251, 84, 174, 0.5) 0.32%, rgba(153, 42, 210, 0.5) 52.68%, rgba(63, 4, 243, 0.5) 100.25%)',
            }}
          >
            <p className="text-[#FFFFFF] text-sm font-medium">{`Score: ${Number(
              score,
            ).toFixed(2)}`}</p>
          </div>
          <div className="flex items-center ml-10">
            {audioUrl && (
              <>
                <button className="flex items-center" onClick={toggleAudio}>
                  {!audioState?.playing ? (
                    <CirclePlay
                      className="w-7 h-7 rounded-full p-1 text-white mb-3"
                      style={{
                        background:
                          'linear-gradient(267.39deg, rgba(255, 96, 182, 1) 0.32%, rgba(41, 96, 253, 1) 100.25%)',
                      }}
                    />
                  ) : (
                    <CirclePause
                      className="w-7 h-7 rounded-full p-1 text-white mb-3"
                      style={{
                        background:
                          'linear-gradient(267.39deg, rgba(255, 96, 182, 1) 0.32%, rgba(41, 96, 253, 1) 100.25%)',
                      }}
                    />
                  )}
                  <p className="dark:text-[#C9A3FF] text-purple-600 text-sm ml-2 mb-3">
                    Audio Available
                  </p>
                </button>
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onEnded={() => setAudioState({ playing: false })}
                />
              </>
            )}
            {videoUrl && (
              <>
                <button
                  className="flex items-center ml-4"
                  onClick={() => {
                    openVideoModal(videoUrl, videoDuration || null);
                  }}
                >
                  <Video
                    className="w-7 h-7 rounded-full p-1 text-white mb-3"
                    style={{
                      background:
                        'linear-gradient(267.39deg, rgba(255, 96, 182, 1) 0.32%, rgba(41, 96, 253, 1) 100.25%)',
                    }}
                  />
                  <p className="dark:text-[#C9A3FF] text-purple-600 text-sm ml-2 mb-3">
                    Video Available
                  </p>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAssessmentCards = () => {
    return assessmentSequence.map((assessment, index) => {
      const score =
        assessment === 'typing'
          ? scores.typing_accuracy || 'NA'
          : scores[`${assessment}_percent`] || 'NA';
      const scoreColor =
        score !== 'NA'
          ? getScoreColor(
              evaluationData?.Interview_final_evaluation?.recommendation,
            )
          : 'text-[#D9D9D9]';
      const isCompleted =
        candidateData[`${assessment}_status`] === STATUSES.COMPLETE ||
        candidateData[`${assessment}_status`] === STATUSES.PASSED;
      const isClickable = isCompleted;

      return (
        <div
          key={index}
          className="border rounded-2xl border-gray-700/50 flex flex-col overflow-hidden w-full max-w-[400px] min-w-[300px] "
          style={{
            cursor: isClickable ? 'pointer' : 'not-allowed',
          }}
          onClick={(e) => {
            if (isClickable) {
              if (assessment === 'interview') {
                setShowProctoringReport(false);
                setShowProctoringDetails(false);
                setSelectedViolationScreenshots(null);
                setshowEvaluationDetails(true);
                setShowTypingReport(false);
                setShowMCQReport(false);
                setShowCodingReport(false);
                setShowBehavioralReport(false);
                setActiveTab('SmartEvaluation');
              } else if (assessment === 'typing') {
                setShowTypingReport(true);
                setShowMCQReport(false);
                setShowCodingReport(false);
                setShowBehavioralReport(false);
                setshowEvaluationDetails(false);
              } else if (assessment === 'mcq') {
                setShowMCQReport(true);
                setShowCodingReport(false);
                setShowTypingReport(false);
                setShowBehavioralReport(false);
                setshowEvaluationDetails(false);
              } else if (assessment === 'coding') {
                setShowCodingReport(true);
                setShowMCQReport(false);
                setShowTypingReport(false);
                setShowBehavioralReport(false);
                setshowEvaluationDetails(false);
              } else if (assessment === 'behavioral') {
                setShowBehavioralReport(true);
                setShowMCQReport(false);
                setShowCodingReport(false);
                setShowTypingReport(false);
                setshowEvaluationDetails(false);
              }
            }
          }}
        >
          <div
            className="border-b border-gray-700/50 flex items-center justify-between p-2 sm:p-3 backdrop-blur-2xl rounded-t-2xl"
            style={{
              background:
                'linear-gradient(233.57deg, rgba(217, 217, 217, 0.05) -5.01%, rgba(115, 108, 207, 0.01) 100%)',
            }}
          >
            <h4 className="dark:text-[#C9A3FF] text-textPrimary text-xs sm:text-sm">
              {assessment.toUpperCase()}
            </h4>
            {(assessment === 'interview' ||
              assessment === 'typing' ||
              assessment === 'behavioral') &&
              candidateData[`${assessment}_status`] === STATUSES.COMPLETE &&
              scores[`${assessment}_percent`] !== 'NA' && (
                <button
                  className="dark:text-[#C9A3FF] text-textSecondary  dark:hover:text-purple-500 hover:text-textPrimary text-xs sm:text-sm underline truncate"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (assessment === 'interview') {
                      setshowEvaluationDetails(true);
                      setActiveTab('SmartEvaluation');
                    } else if (assessment === 'typing') {
                      setShowTypingReport(true);
                    } else if (assessment === 'behavioral') {
                      setShowBehavioralReport(true);
                    }
                  }}
                >
                  See Details
                </button>
              )}
          </div>
          <div className="flex items-center justify-between p-2 sm:p-3">
            <div className="flex flex-col items-center border-r border-[#9BA6A14D] px-4 sm:px-6 md:px-10 w-full sm:w-auto py-2 sm:py-0">
              <span className="dark:text-[#A8A8A8] text-textSecondary text-xs sm:text-sm">
                Score
              </span>
              <span
                className={`mt-2 font-medium text-2xl sm:text-3xl ${scoreColor} truncate`}
              >
                {Math.floor(Number(score))}
              </span>
            </div>
            <div className="flex space-x-4 sm:space-x-6 md:space-x-8">
              <div className="flex flex-col">
                <span className="text-xs sm:text-sm dark:text-[#A8A8A8] text-textSecondary">
                  Date
                </span>
                <span className="text-xs sm:text-sm dark:text-[#A8A8A8] text-textSecondary text-wrap max-w-[100px] sm:max-w-[120px]">
                  {candidateData?.invited_date || 'N/A'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs sm:text-sm dark:text-[#A8A8A8] text-textSecondary">
                  Duration
                </span>
                <span className="text-xs sm:text-sm dark:text-[#A8A8A8] text-textSecondary text-wrap max-w-[100px] sm:max-w-[120px]">
                  {assessment === 'interview'
                    ? `${candidateData?.interview_time_limit || 'N/A'} minutes`
                    : assessment === 'mcq'
                      ? `${candidateData?.mcq_time_limit || 'N/A'} minutes`
                      : assessment === 'coding'
                        ? `${candidateData?.coding_time_limit || 'N/A'} minutes`
                        : assessment === 'behavioral'
                          ? `${candidateData?.behavioral_time_limit || 'N/A'} minutes`
                          : `${candidateData?.typing_time_limit || 'N/A'} minutes`}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  const lightChartStyles = {
    '& .MuiChartsAxis-line': {
      stroke: '#1F2937 !important',

      strokeWidth: 1,
    },

    '& .MuiChartsAxis-tick': {
      stroke: '#1F2937',

      strokeWidth: 1,
    },

    '& .MuiChartsAxis-tickLabel': {
      fill: '#1F2937', // Dark color for tick labels

      fontFamily: 'Poppins',

      fontSize: '12px',
    },

    '& .MuiChartsAxis-label': {
      fill: '#1F2937', // Dark color for axis labels

      fontFamily: 'Poppins',

      fontSize: '14px',

      fontWeight: 600, // Optional: make labels stand out more
    },

    '& .MuiChartsLegend-series text': {
      fill: '#1F2937',

      fontFamily: 'Poppins',
    },

    '& .MuiLineElement': {
      strokeWidth: 2,
    },

    '& .MuiMarkElement': {
      fill: '#7C3AED',

      stroke: '#7C3AED',

      strokeWidth: 1,
    },
  };

  const darkChartStyles = {
    '& .MuiChartsAxis-line': {
      stroke: '#D9D9D9 !important',
      strokeWidth: 1,
    },
    '& .MuiChartsAxis-tick': {
      strokeWidth: 1,
    },
    '& .MuiChartsAxis-tickLabel': {
      fill: '#D9D9D9 !important',
      fontFamily: 'Poppins',
      fontSize: '12px',
    },
    '& .MuiChartsAxis-label': {
      fill: '#D9D9D9 !important',
      fontFamily: 'Poppins',
      fontSize: '14px',
    },
    '& .MuiChartsLegend-series text': {
      fill: '#D1D5DB !important',
      fontFamily: 'Poppins',
    },
    '& .MuiLineElement': {
      strokeWidth: 2,
    },
    '& .MuiMarkElement': {
      fill: '#C9A3FF',
      stroke: '#C9A3FF',
      strokeWidth: 1,
    },
  };

  const handleupdateCandidateStatus = async (
    candidate,
    status,
    reason = '',
  ) => {
    if (status === 'REJECTED' && reason.trim() === '') {
      toast.error('Please provide a reason for rejection');
      return;
    }

    const response = await updateCandidateStatusService({
      interview_id: interview_id,
      status,
      reason: reason,
      candidate_name: candidateFromReduxForAPI?.candidate_name,
      candidate_email: candidateFromReduxForAPI?.candidate_email,
      candidate_number:
        candidateFromReduxForAPI?.contact_number != null
          ? String(candidateFromReduxForAPI.contact_number)
          : '',
      hr_owner: candidateFromReduxForAPI?.scheduled_by_name,
      date_of_interview: candidateFromReduxForAPI?.invited_date,
      interview_score:
        candidateFromReduxForAPI?.interview_percent != null
          ? String(candidateFromReduxForAPI.interview_percent)
          : 'NA',
      job_role: candidateFromReduxForAPI?.jdposition,
    });

    const { success, data } = response;

    if (success && status === 'SELECTED') {
      toast.success('Candidate Marked as Selected');
      dispatch(updateCandidateStatus({ candidate }));
      dispatch(addCandidateToShortListedList(data));
      const basePath = portal === 'hr' ? '/hr' : '/org-admin'; // ← respect portal
      navigate(`${basePath}/candidate-management`);
      return;
    }

    if (success && status === 'REJECTED') {
      toast.success('Candidate Marked as Rejected');
      dispatch(updateCandidateStatus({ candidate }));
      dispatch(addCandidateToRejectedList(data));
      const basePath = portal === 'hr' ? '/hr' : '/org-admin'; // ← respect portal
      navigate(`${basePath}/candidate-management`);
    }
  };

  // Authenticity / Plagiarism scores: prefer the new unified proctoring_report
  // format, fall back to the legacy Interview_final_evaluation fields.
  // Use nullish coalescing so a valid 0 from the new format is not discarded.
  const authenticityConfidenceScore =
    evaluationData?.proctoring_report?.authenticity_confidence_score ??
    evaluationData?.Interview_final_evaluation?.authenticity_confidence_score;
  const plagiarismScore =
    evaluationData?.proctoring_report?.plagiarism_score ??
    evaluationData?.Interview_final_evaluation?.plagiarism_score;
  // Plagiarism risk is only shown for the new proctoring_report format; old
  // reports do not surface this line.
  const plagiarismRisk = evaluationData?.proctoring_report?.plagiarism_risk;

  const PLAGIARISM_REVIEW_THRESHOLD = 40;
  const proctoringRecommendation =
    evaluationData?.proctoring_report?.recommendation;
  const integrityFlag =
    evaluationData?.Interview_final_evaluation?.integrity_flag ??
    ['possible misconduct indicators', 'major proctoring violations detected'].includes(
      (proctoringRecommendation || '').toLowerCase(),
    );
  const plagiarismAboveThreshold =
    typeof plagiarismScore === 'number' &&
    plagiarismScore >= PLAGIARISM_REVIEW_THRESHOLD;

  // Hiring headline: pick professional wording from the (reconciled)
  // recommendation, the ORIGINAL recommendation, and the integrity flag.
  //  - Recommended + clean proctoring   → strong overall candidate (green)
  //  - Recommended + possible/major      → good skills, needs integrity review (amber)
  //  - Needs Further Evaluation (any)    → needs further skill evaluation (amber)
  //  - Rejected (any)                    → not recommended (red)
  const finalRecommendation =
    evaluationData?.Interview_final_evaluation?.recommendation;
  const originalRecommendation =
    evaluationData?.Interview_final_evaluation?.original_recommendation ||
    finalRecommendation;
  const recLower = (finalRecommendation || '').toLowerCase();
  const origLower = (originalRecommendation || '').toLowerCase();
  const recState =
    recLower === 'rejected'
      ? 'rejected'
      : origLower === 'recommended' && integrityFlag
        ? 'flagged'
        : origLower === 'recommended'
          ? 'recommended'
          : 'needs_eval';
  const REC_BADGE = {
    recommended: {
      text: 'Recommendation: The candidate is overall strong and a good fit.',
      classes: 'bg-[#1BAA0E26] text-[#1BAA0E] dark:text-[#1BAA0E]',
    },
    flagged: {
      text: 'Recommendation: The candidate shows good skills but needs an integrity review.',
      classes: 'bg-[#FFB20026] text-[#FFB200] dark:text-[#FFB200]',
    },
    needs_eval: {
      text: 'Recommendation: The candidate needs a further secondary evaluation on skills.',
      classes: 'bg-[#FFB20026] text-[#FFB200] dark:text-[#FFB200]',
    },
    rejected: {
      text: 'Recommendation: The candidate is not yet ready and lacks the required skills.',
      classes: 'bg-[#FF303026] text-[#FF3030] dark:text-[#FF3030]',
    },
  };
  const recBadge = REC_BADGE[recState];

  return (
    <>
      <style>{selectableStyles}</style>
      <div
        className={
          isPublic
            ? ''
            : 'min-h-screen px-2 sm:px-2 md:px-4 lg:px-8 mb-8'
        }
      >
        <div className="flex flex-col py-1 gap-2">
          <div className="flex gap-2 ">
            <div className="w-[75%] bg-bgPrimary dark:bg-white/5 rounded-2xl px-3 py-1">
              <div className="flex gap-2 items-center py-2">
                <div className="flex-shrink-0">
                  <div className="flex gap-3 items-center px-2">
                    {can.back && (
                      <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        title="Back"
                      >
                        <ArrowLeft className="w-5 h-5 cursor-pointer dark:text-[#C9A3FF] text-textSecondary" />
                      </button>
                    )}
                  </div>
                  <div className="relative rounded-full h-40 w-40 overflow-hidden ml-4">
                    {evaluationData?.candidate_photo_url ? (
                      <>
                        <img
                          src={evaluationData.candidate_photo_url}
                          alt={candidateData.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {/* Fallback gradient background if image fails */}
                        <div
                          className="absolute inset-0 flex justify-center items-center rounded-full hidden"
                          style={{
                            background:
                              'linear-gradient(267.39deg, #FF60B6 0.32%, #2960FD 100.25%)',
                          }}
                        >
                          <p className="text-4xl font-semibold text-white">
                            {getInitials(candidateData?.name)}
                          </p>
                        </div>
                      </>
                    ) : (
                      <div
                        className="flex justify-center items-center rounded-full h-full w-full"
                        style={{
                          background:
                            'linear-gradient(267.39deg, #FF60B6 0.32%, #2960FD 100.25%)',
                        }}
                      >
                        <p className="text-4xl font-semibold text-white">
                          {getInitials(candidateData?.name)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full bg-white dark:bg-transparent">
                  <div className="flex items-center justify-between p-3 gap-8">
                    {/* Center: Candidate Information */}
                    <div className="flex-1 space-y-3">
                      {/* Name and Role */}
                      <div className="flex justify-between">
                        <div>
                          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                            {candidateData.name}
                          </h2>
                          <p className="text-gray-500 font-light dark:text-[#A8A8A8] text-base mt-0.5 italic mb-2">
                            {candidateData.job_role}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {can.pdf && (
                            <div className='hover:scale-105 transition-transform duration-200' >
                            <PDFGenerator
                              name={candidateData.name}
                              role={candidateData.job_role}
                              scores={scores}
                              stages={stages}
                              interview_id={interview_id}
                              org_id={org_id}
                            />
                            </div>
                          )}
                          {/* {![STATUSES.COMPLETE, STATUSES.PASSED].includes(candidateData.interview_status) && (
                            <button
                              title="Cancel Interview"
                              className="flex items-center font-light gap-2 rounded-full text-red-500 dark:text-red-400 transition-colors"
                              onClick={() => setIsDeleteModalOpen(true)}
                            >
                              <Trash2 className="w-6 h-6" />
                            </button>
                          )} */}
                          {can.share && (
                            <button
                              title="Share Report"
                              className="flex items-center font-light gap-2 rounded-full text-textSecondary transition-colors text-gray-700 dark:text-gray-300 hover:scale-105 transition-transform duration-200"
                              onClick={() => {
                                if (!featureFlags.report_sharing) {
                                  setLimitModal({
                                    isOpen: true,
                                    featureKey: 'report_sharing',
                                    errorCode: 'FEATURE_LOCKED',
                                  });
                                  return;
                                }
                                setShareDialogOpen(true);
                              }}
                            >
                              <ExternalLink className="w-6 h-6" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Details Row 1: Experience, Location, Notice Period, Source */}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600 dark:text-[#A8A8A8]">
                        {candidateData.candidate_experience && (
                          <div className="flex items-center gap-2">
                            <img
                              src="/briefcase_new.png"
                              className="w-4 h-4"
                              alt="Experience"
                            />
                            <span>{candidateData.candidate_experience}</span>
                          </div>
                        )}

                        {candidateData.candidate_location && (
                          <div className="flex items-center gap-2">
                            <img
                              src="/location.png"
                              className="w-4 h-4"
                              alt="Location"
                            />
                            <span>{candidateData.candidate_location}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <span>
                            Notice Period:{' '}
                            {candidateData.notice_period
                              ? candidateData.notice_period
                                  .charAt(0)
                                  .toUpperCase() +
                                candidateData.notice_period.slice(1)
                              : '-'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span>
                            Source:{' '}
                            {candidateData.source_of_application
                              ? candidateData.source_of_application
                                  .charAt(0)
                                  .toUpperCase() +
                                candidateData.source_of_application.slice(1)
                              : '-'}
                          </span>
                        </div>
                      </div>

                      {/* Details Row 2: Email and Phone */}
                      <div className="flex flex-col justify-between gap-x-6 gap-y-2 text-sm">
                        <div className="flex gap-4">
                          {candidateData.candidate_email && (
                            <div className="flex items-center gap-2">
                              <img
                                src="/sms-tracking.png"
                                className="w-4 h-4"
                              ></img>
                              <a
                                href={`mailto:${candidateData.candidate_email}`}
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {candidateData.candidate_email}
                              </a>
                            </div>
                          )}

                          {candidateData?.contact_number && (
                            <div className="flex items-center gap-2">
                              <img
                                src="/call-calling.png"
                                className="w-4 h-4"
                              ></img>
                              <span className="text-gray-600 dark:text-[#A8A8A8]">
                                {candidateData.contact_number}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                        {(can.analysis || can.resume) && (
                        <div className="flex flex-row end py-2 mx-1 flex-1 my-1 gap-4 text-sm">
                              {can.analysis && (
                              <button className={`${isDarkMode === 'dark' ? 'border-btn-dark' : 'border-btn !text-textPrimary'} hover:scale-105 transition-transform duration-200 !py-1.5 !px-7 mt-3`}
                                  onClick={() => {
                                    handleViewAnalysisReport();
                                  }}
                                  >
                                    <span>
                                    View Analysis Report
                                    </span>
                              </button>
                              )}
                              {can.resume && (
                              <button className={`${isDarkMode === 'dark' ? 'border-btn-dark' : 'border-btn !text-textPrimary'} hover:scale-105 transition-transform duration-200 !py-1.5 !px-7 mt-3`}
                                  onClick={() => {
                                    handleViewResume();
                                  }}
                                  >
                                    <span>
                                      Download Resume
                                    </span>
                              </button>
                              )}

                      </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-[25%] bg-bgPrimary dark:bg-white/5 rounded-2xl py-1">
              <div className="space-y-2 px-4">
                <div className="flex items-center mt-1 space-x-2">
                  <span className="dark:text-[#A8A8A8] text-textPrimary text-md font-medium">
                    Introductory Video
                  </span>
                </div>
                {evaluationData?.introduction_video_url ? (
                  <div
                    className="relative cursor-pointer"
                    onClick={() => {
                      openVideoModal(evaluationData.introduction_video_url);
                    }}
                  >
                    <video
                      src={evaluationData.introduction_video_url}
                      className="w-full h-48 object-cover rounded-lg"
                      preload="metadata"
                      muted
                      playsInline
                    />
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                      <Play className="w-10 h-10 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No video available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 items-stretch">
            {/* LEFT — Interview Outcome & Hiring Decision */}
            <div className="flex flex-col justify-between w-[75%] bg-bgPrimary dark:bg-white/5 rounded-2xl px-4 py-2 space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-2 mt-1">
                  <h3 className="font-medium text-base dark:text-[#C1A0D0] text-textPrimary">
                    Interview Outcome & Hiring Decision
                  </h3>
                </div>
                {evaluationData?.Interview_final_evaluation?.recommendation ? (
                  <>
                    <div
                      className={`w-fit py-2 px-3 rounded-xl font-medium mb-2 text-base flex items-center gap-2 ${recBadge.classes}`}
                    >
                      <span className="font-medium text-sm tracking-wide">
                        {recBadge.text}
                      </span>
                    </div>
                    {evaluationData?.Interview_final_evaluation
                      ?.recommendation_reasoning_bullets?.length > 0 ? (
                      <div
                        className={`mt-3 space-y-2 ${integrityFlag ? 'opacity-100' : ''}`}
                      >
                        {[
                          ...evaluationData.Interview_final_evaluation
                            .recommendation_reasoning_bullets,
                        ]
                          .sort((a, b) => {
                            if (a.sentiment === b.sentiment) return 0;
                            if (a.sentiment === 'positive') return -1; // positives first
                            return 1; // negatives after
                          })
                          .map((bullet, index) => (
                            <div key={index} className="flex items-start gap-3">
                              {bullet.sentiment === 'positive' ? (
                                <CircleCheck className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                              ) : (
                                <CircleX className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                              )}
                              <p className="dark:text-gray-400 text-textSecondary text-[14.5px]">
                                {bullet.point}
                              </p>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p
                        className={`dark:text-gray-300 text-textSecondary text-[15px] mt-3 ${integrityFlag ? 'opacity-60' : ''}`}
                      >
                        {
                          evaluationData?.Interview_final_evaluation
                            ?.recommendation_reason
                        }
                      </p>
                    )}

                    {can.status && activeMainTab != 'DECISION' && (
                      <div className="flex flex-row items-center py-2 mx-1 flex-1 mt-2 gap-x-4">
                        <button
                          className="button hover:scale-105 transition-transform duration-200 !px-6 !py-1.5 text-sm  !text-white transition-colors"
                          onClick={() => {
                            handleupdateCandidateStatus(
                              candidateData,
                              'SELECTED',
                            );
                          }}
                        >
                          <span className="text-white">Select</span>
                        </button>
                        <button
                          className="px-6 py-1.5 text-sm rounded-full border border-red-500 text-red-500 hover:bg-red-500 hover:text-white dark:border-red-400 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-colors hover:scale-105 transition-transform duration-200"
                          onClick={() => {
                            setShowReasonModel(true);
                            setSelectedCandidate(candidateFromReduxForAPI);
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center py-10">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      The interview needs to be completed
                    </p>
                  </div>
                )}
              </div>
            </div>
            {/* RIGHT — Proctoring Report + Remarks stacked */}
            <div className="w-[25%] space-y-2">
              <div className="bg-bgPrimary dark:bg-white/5 rounded-2xl px-4 py-4">
                <div className="flex flex-row justify-between items-start gap-3 mb-4">
                  <div className="flex flex-col items-start gap-2">
                    <h3 className="font-medium text-base dark:text-[#C1A0D0] text-textPrimary mb-0">
                      Proctoring Report
                    </h3>
                    {/* {needsProctoringReview && (
                      <span className="px-3 py-0.5 rounded-full text-[12px] font-normal border border-[#fdbc23] bg-[#FFB20026] text-[#FFB200] dark:text-[#FFB200]">
                        Needs Review
                      </span>
                    )} */}
                  </div>
                  {(candidateData.interview_status === STATUSES.COMPLETE ||
                      candidateData.coding_status === STATUSES.COMPLETE) && (
                        <button
                          className={`${isDarkMode === 'dark' ? 'border-btn-dark' : 'border-btn !text-textPrimary'} hover:scale-105 transition-transform duration-200 justify-end !py-1 !px-2`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setProctoringModalOpen(true);
                            fetchCodingViolationData();
                            fetchSecondaryCameraData();
                            // default to Coding tab if interview not done
                            if (candidateData.interview_status !== STATUSES.COMPLETE) {
                              setProctoringModalTab('coding');
                            }
                          }}
                        >
                          <span>Review</span>
                        </button>
                  )}
                </div>

                {proctoringRecommendation ||
                typeof plagiarismScore === 'number' ? (
                  <div className="mt-8 mb-4">
                    {/* Proctoring verdict (Major / Possible / Minor / No verified violations) */}
                    {proctoringRecommendation && (
                      <p
                        className={`text-[14.5px] font-normal mb-3 ml-1.5 dark:text-gray-400 text-textSecondary}`}
                      >
                        {verdictTitle(evaluationData?.proctoring_report)}
                      </p>
                    )}

                    {/* Plagiarism score */}
                    {typeof plagiarismScore === 'number' && (
                      <div className="flex flex-row items-center gap-5 ml-1.5 mt-4">
                        <div
                          className={`h-[54px] w-[54px] shrink-0 rounded-full border flex items-center justify-center ${
                            plagiarismAboveThreshold
                              ? 'border-[#DC000066] bg-[#DC00001A]'
                              : 'border-[#34B53A66] bg-[#34B53A1A]'
                          }`}
                        >
                          <span
                            className={`text-[15px] font-normal ${
                              plagiarismAboveThreshold
                                ? 'text-[#DC0000]'
                                : 'text-[#34B53A]'
                            }`}
                          >
                            {Math.floor(Number(plagiarismScore))}%
                          </span>
                        </div>
                        <p className="text-[14px] font-normal leading-snug dark:text-gray-300 text-textSecondary">
                          Plagiarism
                          <br />
                          Detection Score
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center">
                      No proctoring assessment available yet
                    </p>
                  </div>
                )}
              </div>

              {/* Remarks card */}
              {can.remarks && (
              <div className="bg-bgPrimary dark:bg-white/5 rounded-2xl px-4 py-4">
                <div className="flex flex-row justify-between items-center gap-3">
                  <h3 className="font-medium text-base dark:text-[#C1A0D0] text-textPrimary mb-0">
                    Remarks
                  </h3>
                  <button
                    className={`${isDarkMode === 'dark' ? 'border-btn-dark' : 'border-btn !text-textPrimary'} hover:scale-105 transition-transform duration-200 justify-end !py-1 !px-2`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAddRemarkModalOpen(true);
                    }}
                  >
                    <span>View / Add</span>
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
        <div className="min-h-screen mt-4">
          <div className="bg-bgPrimary dark:bg-white/5 rounded-2xl overflow-hidden">
            <h3 className="font-medium text-md dark:text-[#C1A0D0] text-textPrimary mb-4 px-5 pt-4">
              Individual Assessment Performance
            </h3>
            {/* Dynamic Assessment Tabs - Only show if multiple assessments exist */}
            {(() => {
              const activeAssessments = [
                candidateData.interview_status &&
                  candidateData.interview_status !== 'NA',
                candidateData.mcq_status && candidateData.mcq_status !== 'NA',
                candidateData.coding_status &&
                  candidateData.coding_status !== 'NA',
                candidateData.typing_status &&
                  candidateData.typing_status !== 'NA',
                candidateData.behavioral_status &&
                  candidateData.behavioral_status !== 'NA',
              ].filter(Boolean).length;

              const showTabs = activeAssessments > 1;

              // Check if all tabs are disabled (none are completed)
              // Check if all tabs are disabled (none are completed)
              const existingAssessments = [
                candidateData.interview_status &&
                  candidateData.interview_status !== 'NA',
                candidateData.mcq_status && candidateData.mcq_status !== 'NA',
                candidateData.coding_status &&
                  candidateData.coding_status !== 'NA',
                candidateData.typing_status &&
                  candidateData.typing_status !== 'NA',
                candidateData.behavioral_status &&
                  candidateData.behavioral_status !== 'NA',
              ].filter(Boolean);

              const disabledAssessments = [
                candidateData.interview_status &&
                candidateData.interview_status !== 'NA'
                  ? candidateData.interview_status !== STATUSES.COMPLETE &&
                    candidateData.interview_status !== STATUSES.PASSED
                  : null,
                candidateData.mcq_status && candidateData.mcq_status !== 'NA'
                  ? candidateData.mcq_status !== STATUSES.COMPLETE &&
                    candidateData.mcq_status !== STATUSES.PASSED
                  : null,
                candidateData.coding_status &&
                candidateData.coding_status !== 'NA'
                  ? candidateData.coding_status !== STATUSES.COMPLETE &&
                    candidateData.coding_status !== STATUSES.PASSED
                  : null,
                candidateData.typing_status &&
                candidateData.typing_status !== 'NA'
                  ? candidateData.typing_status !== STATUSES.COMPLETE &&
                    candidateData.typing_status !== STATUSES.PASSED
                  : null,
                candidateData.behavioral_status &&
                candidateData.behavioral_status !== 'NA'
                  ? candidateData.behavioral_status !== STATUSES.COMPLETE &&
                    candidateData.behavioral_status !== STATUSES.PASSED
                  : null,
              ].filter((item) => item !== null);

              const allTabsDisabled =
                existingAssessments.length > 0 &&
                disabledAssessments.length > 0 &&
                disabledAssessments.every((disabled) => disabled === true);

              return showTabs ? (
                <>
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-0">
                      {/* Technical Interview Tab - Always show if interview_status exists */}
                      {candidateData.interview_status &&
                        candidateData.interview_status !== 'NA' && (
                          <button
                            onClick={() => {
                              if (
                                candidateData.interview_status ===
                                  STATUSES.COMPLETE ||
                                candidateData.interview_status ===
                                  STATUSES.PASSED
                              ) {
                                setShowTypingReport(false);
                                setShowMCQReport(false);
                                setShowCodingReport(false);
                                setShowBehavioralReport(false);
                                setshowEvaluationDetails(true);
                                setActiveTab('SmartEvaluation');
                              }
                            }}
                            disabled={
                              candidateData.interview_status !==
                                STATUSES.COMPLETE &&
                              candidateData.interview_status !== STATUSES.PASSED
                            }
                            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                              candidateData.interview_status !==
                                STATUSES.COMPLETE &&
                              candidateData.interview_status !== STATUSES.PASSED
                                ? 'border-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : showEvaluationDetails
                                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            Technical Interview
                          </button>
                        )}

                      {/* MCQ Test Tab - Only show if mcq_status exists */}
                      {candidateData.mcq_status &&
                        candidateData.mcq_status !== 'NA' && (
                          <button
                            onClick={() => {
                              if (
                                candidateData.mcq_status ===
                                  STATUSES.COMPLETE ||
                                candidateData.mcq_status === STATUSES.PASSED
                              ) {
                                setShowTypingReport(false);
                                setshowEvaluationDetails(false);
                                setShowCodingReport(false);
                                setShowBehavioralReport(false);
                                setShowMCQReport(true);
                              }
                            }}
                            disabled={
                              candidateData.mcq_status !== STATUSES.COMPLETE &&
                              candidateData.mcq_status !== STATUSES.PASSED
                            }
                            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                              candidateData.mcq_status !== STATUSES.COMPLETE &&
                              candidateData.mcq_status !== STATUSES.PASSED
                                ? 'border-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : showMCQReport
                                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            MCQ Test
                          </button>
                        )}

                      {/* Coding Test Tab - Only show if coding_status exists */}
                      {candidateData.coding_status &&
                        candidateData.coding_status !== 'NA' && (
                          <button
                            onClick={() => {
                              if (
                                candidateData.coding_status ===
                                  STATUSES.COMPLETE ||
                                candidateData.coding_status === STATUSES.PASSED
                              ) {
                                setShowTypingReport(false);
                                setshowEvaluationDetails(false);
                                setShowMCQReport(false);
                                setShowBehavioralReport(false);
                                setShowCodingReport(true);
                              }
                            }}
                            disabled={
                              candidateData.coding_status !==
                                STATUSES.COMPLETE &&
                              candidateData.coding_status !== STATUSES.PASSED
                            }
                            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                              candidateData.coding_status !==
                                STATUSES.COMPLETE &&
                              candidateData.coding_status !== STATUSES.PASSED
                                ? 'border-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : showCodingReport
                                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            Coding Test
                          </button>
                        )}

                      {/* Typing Test Tab - Only show if typing_status exists */}
                      {candidateData.typing_status &&
                        candidateData.typing_status !== 'NA' && (
                          <button
                            onClick={() => {
                              if (
                                candidateData.typing_status ===
                                  STATUSES.COMPLETE ||
                                candidateData.typing_status === STATUSES.PASSED
                              ) {
                                setShowTypingReport(true);
                                setshowEvaluationDetails(false);
                                setShowMCQReport(false);
                                setShowCodingReport(false);
                                setShowBehavioralReport(false);
                              }
                            }}
                            disabled={
                              candidateData.typing_status !==
                                STATUSES.COMPLETE &&
                              candidateData.typing_status !== STATUSES.PASSED
                            }
                            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                              candidateData.typing_status !==
                                STATUSES.COMPLETE &&
                              candidateData.typing_status !== STATUSES.PASSED
                                ? 'border-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : showTypingReport
                                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            Typing Test
                          </button>
                        )}

                      {candidateData.behavioral_status &&
                        candidateData.behavioral_status !== 'NA' && (
                          <button
                            onClick={() => {
                              if (
                                candidateData.behavioral_status ===
                                  STATUSES.COMPLETE ||
                                candidateData.behavioral_status === STATUSES.PASSED
                              ) {
                                setShowBehavioralReport(true);
                                setshowEvaluationDetails(false);
                                setShowMCQReport(false);
                                setShowCodingReport(false);
                                setShowTypingReport(false);
                              }
                            }}
                            disabled={
                              candidateData.behavioral_status !==
                                STATUSES.COMPLETE &&
                              candidateData.behavioral_status !== STATUSES.PASSED
                            }
                            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                              candidateData.behavioral_status !==
                                STATUSES.COMPLETE &&
                              candidateData.behavioral_status !== STATUSES.PASSED
                                ? 'border-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : showBehavioralReport
                                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            Behavioural Assessment
                          </button>
                        )}
                    </div>
                  </div>

                  {/* Show message if all tabs are disabled */}
                  {allTabsDisabled && (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                          Assessment Pending
                        </p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                          The candidate has not completed any assessments yet
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : null;
            })()}

            {/* Tab Content Area */}
            <div className="">
              {showEvaluationDetails ? (
                <div>
                  {/* Sub-tabs for Technical Interview: AI Insights & Candidate Response */}
                  <div className="flex items-center mb-6 mt-4 px-4">
                    <div className="rounded-full flex space-x-2 px-1 py-1 border border-gray-600 dark:glass-effect bg-white dark:bg-transparent">
                      <button
                        onClick={() => setActiveTab('SmartEvaluation')}
                        className={
                          activeTab === 'SmartEvaluation'
                            ? `py-2 px-4 bg-gradient-to-l from-[#FB54AE] to-[#2F64FF] rounded-full border-none text-white text-xs sm:text-sm font-medium w-full sm:w-auto`
                            : `py-2 px-4 hover:from-blue-700 hover:to-indigo-700 dark:text-white text-textPrimary rounded-2xl font-medium text-xs sm:text-sm w-full sm:w-auto`
                        }
                      >
                        <span>AI Insights</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('CandidateResponse')}
                        className={
                          activeTab === 'CandidateResponse'
                            ? `py-2 px-4 bg-gradient-to-l from-[#FB54AE] to-[#2F64FF] rounded-full border-none text-white text-xs sm:text-sm font-medium w-full sm:w-auto`
                            : `py-2 px-4 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 dark:text-white text-textPrimary rounded-2xl font-medium text-xs sm:text-sm w-full sm:w-auto`
                        }
                      >
                        <span>Candidate Response</span>
                      </button>
                    </div>
                  </div>

                  {/* Technical Interview Content */}
                  {activeTab === 'SmartEvaluation' ? (
                    <div>
                      <>
                        {evaluationData?.interview_percent > 20 ? (
                          <>
                            <div className="mb-8">
                              <div className="flex items-stretch gap-8 bg-white dark:bg-transparent py-3">
                                {/* Left: Score Circle - 30% width, centered */}
                                <div className="w-[18%] flex items-center justify-center">
                                  <div
                                    className="relative flex-shrink-0"
                                    style={{ width: 160, height: 160 }}
                                  >
                                    <CircularProgressbar
                                      value={
                                        Number(
                                          evaluationData?.interview_percent,
                                        ) || 0
                                      }
                                      text={`${Math.floor(
                                        Number(
                                          evaluationData?.interview_percent,
                                        ) || 0,
                                      )}%`}
                                      circleRatio={0.75}
                                      styles={buildStyles({
                                        rotation: 1 / 2 + 1 / 8,
                                        strokeLinecap: 'butt',
                                        trailColor: getScoreColorGradient2(
                                          evaluationData
                                            ?.Interview_final_evaluation
                                            ?.recommendation,
                                        ),
                                        pathColor: getPathColor2(
                                          evaluationData
                                            ?.Interview_final_evaluation
                                            ?.recommendation,
                                        ),
                                        textColor: `${isDarkMode === 'dark' ? '#FFFFFF' : '#1F2937'}`,
                                        textSize: '24px',
                                        fontWeight: '600',
                                      })}
                                    />
                                    <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                                      Score
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Key Metrics Grid - 70% width */}
                                <div className="w-[75%] justify-center flex flex-col gap-2.5">
                                  {evaluationData?.Interview_final_evaluation
                                    ?.top_skills && (
                                    <>
                                      <div className="flex items-center">
                                        <CircleCheck className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                                        <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                          Top Skills
                                        </span>
                                        <div className="ml-3 flex-1 border dark:border-[#52525280]"></div>
                                      </div>
                                      <div className="flex items-center text-sm dark:text-[#A8A8A8] text-textSecondary space-x-4">
                                        {evaluationData
                                          ?.Interview_final_evaluation
                                          ?.top_skills.length
                                          ? evaluationData?.Interview_final_evaluation?.top_skills.map(
                                              (skill, i) => (
                                                <div className="border px-4 py-2 rounded-full border-[rgba(0,255,0,0.3)] dark:text-[#34B53A] text-green-600 bg-[rgba(0,255,0,0.1)]">
                                                  <span key={i}>{skill}</span>
                                                </div>
                                              ),
                                            )
                                          : null}
                                      </div>
                                    </>
                                  )}
                                  {evaluationData?.Interview_final_evaluation
                                    ?.improvement_areas && (
                                    <>
                                      <div className="flex items-center mt-6">
                                        <TriangleAlert className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                                        <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                          Improvement Areas
                                        </span>
                                        <div className="ml-3 flex-1 border dark:border-[#52525280]"></div>
                                      </div>
                                      <div className="flex text-sm dark:text-[#A8A8A8] text-textSecondary space-x-4">
                                        {evaluationData
                                          ?.Interview_final_evaluation
                                          ?.improvement_areas.length
                                          ? evaluationData?.Interview_final_evaluation?.improvement_areas.map(
                                              (skill, i) => (
                                                <div className="border px-4 py-2 rounded-full border-[rgba(245,158,11,0.3)] dark:text-[#f59e0b] text-orange-600 bg-[rgba(245,158,11,0.1)]">
                                                  <span key={i}>{skill}</span>
                                                </div>
                                              ),
                                            )
                                          : null}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <>
                              <div className="mt-5 flex items-center pl-6">
                                <ChartNoAxesColumn className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                                <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                  Skill Performance Breakdown
                                </span>
                                <div className="flex-1 border dark:border-[#52525280] ml-3 mr-16"></div>
                              </div>
                              <div>
                                <ScoreDisplay
                                  score_value={domainAverageScores}
                                />
                              </div>
                            </>
                            <>
                              <div className="mt-5 flex items-center pl-6">
                                <Clock3 className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                                <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                  Soft Skills
                                </span>
                                <div className="flex-1 border dark:border-[#52525280] ml-3 mr-16"></div>
                              </div>
                              <div>
                                <ScoreDisplay
                                  score_value={
                                    evaluationData?.Interview_final_evaluation
                                      ?.overall_score
                                  }
                                />
                              </div>
                            </>
                            {/* {evaluationData?.Interview_final_evaluation
                          ?.top_skills && (
                          <>
                            <div className="mt-5 flex items-center pl-6">
                              <CircleCheck className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                              <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                Top Skills
                              </span>
                              <div className="flex-1 border dark:border-[#52525280] ml-3"></div>
                            </div>
                            <div className="flex items-center text-sm dark:text-[#A8A8A8] text-textSecondary mt-4 px-6 space-x-4">
                              {evaluationData?.Interview_final_evaluation
                                ?.top_skills.length
                                ? evaluationData?.Interview_final_evaluation?.top_skills.map(
                                    (skill, i) => (
                                      <div className="border px-4 py-2 rounded-full border-[rgba(0,255,0,0.3)] dark:text-[#34B53A] text-green-600 bg-[rgba(0,255,0,0.1)]">
                                        <span key={i}>{skill}</span>
                                      </div>
                                    )
                                  )
                                : null}
                            </div>
                          </>
                        )} */}
                            {/* {evaluationData?.Interview_final_evaluation
                          ?.improvement_areas && (
                          <>
                            <div className="mt-5 flex items-center pl-6">
                              <TriangleAlert className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                              <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                Improvement Areas
                              </span>
                              <div className="flex-1 border dark:border-[#52525280] ml-3"></div>
                            </div>
                            <div className="flex text-sm dark:text-[#A8A8A8] text-textSecondary mt-4 px-6 space-x-4">
                              {evaluationData?.Interview_final_evaluation
                                ?.improvement_areas.length
                                ? evaluationData?.Interview_final_evaluation?.improvement_areas.map(
                                    (skill, i) => (
                                      <div className="border px-4 py-2 rounded-full border-[rgba(245,158,11,0.3)] dark:text-[#f59e0b] text-orange-600 bg-[rgba(245,158,11,0.1)]">
                                        <span key={i}>{skill}</span>
                                      </div>
                                    )
                                  )
                                : null}
                            </div>
                          </>
                        )} */}
                            {evaluationData?.Interview_final_evaluation
                              ?.future_potential && (
                              <>
                                <div className="mt-5 flex items-center pl-6">
                                  <Shield className="w-5 h-5 text-[#C9A3FF]" />
                                  <span className="text-[#C9A3FF] text-sm ml-2">
                                    Future Potential
                                  </span>
                                  <div className="flex-1 border border-[#52525280] ml-3"></div>
                                </div>
                                <div className="text-sm text-[#A8A8A8] mt-4 px-6">
                                  {
                                    evaluationData?.Interview_final_evaluation
                                      ?.future_potential
                                  }
                                </div>
                              </>
                            )}
                            {evaluationData?.Interview_final_evaluation
                              ?.hidden_insights && (
                              <>
                                <div className="mt-7 flex items-center pl-6">
                                  <Award className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                                  <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                    Hidden Insights
                                  </span>
                                  <div className="flex-1 border dark:border-[#52525280] ml-3 mr-16"></div>
                                </div>
                                <div className="text-sm dark:text-[#A8A8A8] text-textSecondary  mt-4 px-6">
                                  {
                                    evaluationData?.Interview_final_evaluation
                                      ?.hidden_insights
                                  }
                                </div>
                              </>
                            )}
                            {evaluationData?.Interview_final_evaluation
                              ?.red_flags && (
                              <>
                                <div className="mt-7 flex items-center pl-6">
                                  <Flag className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                                  <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                    Red Flags
                                  </span>
                                  <div className="flex-1 border dark:border-[#52525280] ml-3 mr-16"></div>
                                </div>
                                <div className="text-sm dark:text-[#A8A8A8] text-textSecondary mt-4 px-6">
                                  {
                                    evaluationData?.Interview_final_evaluation
                                      ?.red_flags
                                  }
                                </div>
                              </>
                            )}
                            {evaluationData?.Interview_final_evaluation
                              ?.recommended_next_step && (
                              <>
                                <div className="mt-7 flex items-center pl-6">
                                  <Footprints className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary]" />
                                  <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                    Recommended Next Step
                                  </span>
                                  <div className="flex-1 border dark:border-[#52525280] ml-3 mr-16"></div>
                                </div>
                                <div className="text-sm dark:text-[#A8A8A8] text-textSecondary mt-4 px-6 mb-10">
                                  {
                                    evaluationData?.Interview_final_evaluation
                                      ?.recommended_next_step
                                  }
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <div className="mt-5 m-6">
                            <div className="flex flex-col items-center justify-center border border-gray-700/50 py-10 rounded-2xl">
                              <LockKeyhole className="text-[#C9A3FF] w-10 h-10" />
                              <span className="dark:text-[#A8A8A8] text-textPrimary text-lg my-2 font-medium">
                                Smart Evaluation Locked
                              </span>
                              <span className="dark:text-[#A8A8A8] text-textSecondary text-sm my-2">
                                Insufficient data available to display Smart
                                Evaluation.
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    </div>
                  ) : (
                    <div>
                      <div
                        ref={questionsContainerRef}
                        className="mt-5 px-6 max-h-screen overflow-y-auto relative "
                      >
                        <button
                          onClick={scrollToTop}
                          className={`fixed bottom-6 right-6 bg-[#C9A3FF] text-white p-3 rounded-full shadow-lg hover:bg-[#B68FFF] transition-all duration-300 z-20 ${
                            isScrolled
                              ? 'opacity-100 visible translate-y-0'
                              : 'opacity-0 invisible translate-y-4'
                          }`}
                          aria-label="Scroll to top"
                        >
                          <ChevronUp className="w-6 h-6" />
                        </button>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="dark:text-[#C9A3FF] text-textSecondary text-sm">
                            Questions & Responses
                          </h3>
                          <div className="flex items-center space-x-4">
                            {evaluationData?.translatable && (
                              <button
                                onClick={handleToggleEnglish}
                                disabled={isTranslating}
                                className="text-sm border rounded-full px-4 py-2 cursor-pointer dark:glass-effect bg-bgPrimary dark:text-white text-textSecondary whitespace-nowrap disabled:opacity-60"
                              >
                                {isTranslating
                                  ? 'Translating…'
                                  : showEnglish
                                  ? 'View in Original'
                                  : 'View in English'}
                              </button>
                            )}
                            <div className="relative inline-block min-w-60">
                              <div
                                className="flex justify-between items-center dark:text-white text-textSecondary border rounded-full p-2 cursor-pointer dark:glass-effect bg-bgPrimary px-4"
                                onClick={() => setIsDomainOpen(!isDomainOpen)}
                              >
                                <span
                                  style={{
                                    maxWidth: '150px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {selectedDomain}
                                </span>
                                {!isDomainOpen && selectedDomain === 'All' ? (
                                  <ChevronDown className="w-5 h-5" />
                                ) : (
                                  <CircleX
                                    className="w-5 h-5"
                                    onClick={() => {
                                      setIsDomainOpen(!isDomainOpen);
                                      setSelectedDomain('All');
                                    }}
                                  />
                                )}
                              </div>
                              {isDomainOpen && (
                                <div className="absolute top-full left-0 w-full rounded-md shadow-lg z-10  max-h-60 overflow-y-auto mt-2 bg-bgSecondary text-textPrimary dark:text-white dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:backdrop-blur-[500px] dark:max-h-[300px] ">
                                  <ul>
                                    {domains.map((domain, index) => (
                                      <li
                                        key={index}
                                        className="px-4 py-2 dark:hover:bg-gray-600 hover:bg-gray-200 cursor-pointer"
                                        onClick={() => {
                                          setSelectedDomain(domain);
                                          setIsDomainOpen(false);
                                        }}
                                      >
                                        {domain}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                            {(evaluationData?.video_urls?.length > 0 ||
                              evaluationData?.live_avatar_recording_url) && (
                              <Button
                                onClick={() =>
                                  evaluationData?.live_avatar_recording_url &&
                                  !evaluationData?.video_urls?.length
                                    ? setLiveAvatarRecordingOpen(true)
                                    : setVideoLibraryOpen(true)
                                }
                                sx={{
                                  color: '#C9A3FF',
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: 'rgba(201, 163, 255, 0.1)',
                                  },
                                }}
                              >
                                <Video className="w-5 h-5 mr-2" />
                                Watch All Videos
                              </Button>
                            )}
                          </div>
                        </div>
                        {/* <ThemeProvider theme={theme}> */}
                        <LineChart
                          xAxis={[
                            {
                              data: filteredQuestions.map((i) => i + 1),
                              label: 'Question Number',
                            },
                          ]}
                          yAxis={[
                            {
                              label: 'Score',
                            },
                          ]}
                          series={[
                            {
                              data: filteredQuestions.map(
                                (i) => Number(evaluationData?.scores[i]) || 0,
                              ),
                              color: '#C9A3FF',
                              showMark: true,
                            },
                          ]}
                          height={300}
                          margin={{ top: 20, bottom: 50, left: 50, right: 20 }}
                          sx={{
                            ...(isDarkMode === 'dark'
                              ? darkChartStyles
                              : lightChartStyles),

                            /* FORCE SVG text colors */
                            '& .MuiChartsAxis-tickLabel, & .MuiChartsAxis-label':
                              {
                                fill:
                                  isDarkMode === 'dark'
                                    ? '#D9D9D9 !important'
                                    : '#1F2937 !important',
                                fontFamily: 'Poppins !important',
                              },

                            '& .MuiChartsLegend-series text': {
                              fill:
                                isDarkMode === 'dark'
                                  ? '#D1D5DB !important'
                                  : '#1F2937 !important',
                            },
                          }}
                          onMarkClick={(event, elements) => {
                            if (elements?.dataIndex !== undefined) {
                              const questionIndex =
                                filteredQuestions[elements.dataIndex];
                              const questionRef =
                                questionRefs.current[questionIndex];
                              if (questionRef) {
                                questionRef.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start',
                                });
                              }
                            }
                          }}
                        />

                        {/* </ThemeProvider> */}
                        {selectedDomain !== 'All' && (
                          <div className="flex items-center justify-between">
                            <p className="text-[#A8A8A8] text-sm">
                              {selectedDomain}
                            </p>
                            <p className="text-[#A8A8A8] text-sm">{`Average Score: ${
                              domainAverageScores[selectedDomain]
                                ? Number(
                                    domainAverageScores[selectedDomain],
                                  ).toFixed(2)
                                : 'N/A'
                            }`}</p>
                          </div>
                        )}

                        {filteredQuestions.map((index) => (
                          <div
                            key={index}
                            ref={(el) => (questionRefs.current[index] = el)}
                          >
                            <QuestionAnswerCard
                              key={index}
                              questionNo={index + 1}
                              question={evaluationData?.questions[index]}
                              answer={evaluationData?.answers[index]}
                              questionEn={evaluationData?.questions_en ? evaluationData.questions_en[index] : null}
                              answerEn={evaluationData?.answers_en ? evaluationData.answers_en[index] : null}
                              showEnglish={showEnglish}
                              followUpsEn={evaluationData?.follow_ups_en ? evaluationData.follow_ups_en[index] : null}
                              domain={evaluationData?.domain_or_skill[index]}
                              score={evaluationData?.scores[index]}
                              remark={evaluationData?.remarks[index]}
                              audioUrl={evaluationData?.audio_urls[index]}
                              videoUrl={evaluationData?.video_urls[index]}
                              videoDuration={evaluationData?.duration[index]}
                              follow_ups={evaluationData?.follow_ups?.[index]}
                              openVideoModal={openVideoModal}
                              currentlyPlayingAudio={currentlyPlayingAudio}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : showTypingReport ? (
                <div>
                  <TypingTestReport
                    name={candidateData.name}
                    interviewId={interview_id}
                    onCloseReport={() => setShowTypingReport(false)}
                    data={isPublic ? evaluationData?.typing_test_data : undefined}
                  />
                </div>
              ) : showMCQReport ? (
                <div className="px-6 py-4 max-h-screen overflow-y-auto">
                  <MCQReport
                    name={candidateData.name}
                    mcq_percent={scores.mcq_percent}
                    interviewId={interview_id}
                    data={isPublic ? evaluationData?.mcq_result : undefined}
                  />
                </div>
              ) : showCodingReport ? (
                <div className="px-6 py-4 max-h-screen overflow-y-auto">
                  <CodingTestReport
                    name={candidateData.name}
                    coding_percent={scores.coding_percent}
                    interviewId={interview_id}
                    data={isPublic ? evaluationData?.coding_problems : undefined}
                  />
                </div>
              ) : showBehavioralReport ? (
                <div className="px-6 py-4 max-h-screen overflow-y-auto">
                  <BehavioralResult
                    name={candidateData.name}
                    behavioral_percent={scores.behavioral_percent}
                    interviewId={interview_id}
                    data={isPublic ? evaluationData?.behavioral_result : undefined}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* Proctoring Report Modal */}
      <Dialog
        open={proctoringModalOpen}
        onClose={() => {
          setProctoringModalOpen(false);
          setShowProctoringDetails(false);
          setSelectedViolationScreenshots(null);
          setProctoringModalTab('interview');
          setShowCodingProctoringDetails(false);
          setSelectedCodingViolationScreenshots(null);
        }}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            background: `${isDarkMode === 'dark' ? 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))' : '#FFFFFF'}`,
            backdropFilter: 'blur(50px)',
            WebkitBackdropFilter: 'blur(50px)',
            borderRadius: '12px',
            // A fixed size: switching between the report, the detailed analysis
            // and screenshots changes the content, never the modal.
            maxWidth: '1040px',
            height: '85vh',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
          }}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-[18px] dark:text-[#C1A0D0] text-textPrimary mb-0">
              Proctoring Report Details
            </h3>
            <button
              onClick={() => {
                setProctoringModalOpen(false);
                setShowProctoringDetails(false);
                setSelectedViolationScreenshots(null);
                setProctoringModalTab('interview');
                setShowCodingProctoringDetails(false);
                setSelectedCodingViolationScreenshots(null);
              }}
              className="p-1 rounded-md dark:text-gray-400 text-gray-600 hover:dark:text-white hover:text-gray-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {/* ── Tab bar ───────────────────────────────────────────────────────── */}
          {(() => {
            const proctoringTabs = [
              { key: 'interview', label: 'Interview' },
              // Only show the Coding Round tab when a coding round exists,
              // matching the assessment tabs shown below.
              ...(candidateData.coding_status &&
              candidateData.coding_status !== 'NA'
                ? [{ key: 'coding', label: 'Coding Round' }]
                : []),
            ];

            // With only one tab there is nothing to switch between, so hide the bar.
            if (proctoringTabs.length < 2) return null;

            return (
              <div className={`flex border-b ${isDarkMode === 'dark' ? 'border-gray-700' : 'border-gray-200'} px-6 pt-2`}>
                {proctoringTabs.map(({ key, label }) => {
                  const active = proctoringModalTab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setProctoringModalTab(key);
                        setShowProctoringDetails(false);
                        setSelectedViolationScreenshots(null);
                        setShowCodingProctoringDetails(false);
                        setSelectedCodingViolationScreenshots(null);
                      }}
                      className={`relative px-5 py-3 text-sm font-medium transition-colors ${
                        active
                          ? 'dark:text-[#C9A3FF] text-purple-700'
                          : 'dark:text-gray-400 text-gray-500 hover:dark:text-gray-200 hover:text-gray-700'
                      }`}
                    >
                      {label}
                      {active && (
                        <span
                          className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                          style={{ background: 'linear-gradient(to right, #C9A3FF, #7B5EA7)' }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          <div className="pt-3 pb-6">

            {/* ── INTERVIEW TAB ─────────────────────────────────────────────── */}
            {proctoringModalTab === 'interview' && (
              <>
                {selectedViolationScreenshots && showProctoringDetails ? (
                  <ProctoringScreenshotsView
                    violationType={selectedViolationScreenshots.type}
                    screenshots={selectedViolationScreenshots.screenshots}
                    onBack={() => {
                      setShowProctoringDetails(false);
                      setSelectedViolationScreenshots(null);
                    }}
                  />
                ) : (
                  <>
                    <div className="px-6">
                      {/* The whole report as one card: verdict, summary tiles,
                          answer authenticity, secondary camera, the checks that
                          flagged and the checks that did not. */}
                      <ProctoringSummaryCard
                        report={evaluationData?.proctoring_report}
                        titleClassName={getProctoringStatusColor(
                          evaluationData?.proctoring_report?.recommendation,
                        )}
                        formatViolationType={formatViolationType}
                        proctoringRan={
                          !!evaluationData?.proctoring_report ||
                          hasViolationData ||
                          (evaluationData?.violation_logs || []).length > 0
                        }
                        checkTypes={[...SCREENSHOT_VIOLATIONS, ...COUNT_ONLY_VIOLATIONS]}
                        interviewStartedAt={evaluationData?.startDateTime}
                        violationCounts={
                          evaluationData?.proctoring_report?.verified_violation_counts ||
                          evaluationData?.violation_counts ||
                          {}
                        }
                        violationLogs={evaluationData?.violation_logs || []}
                        screenshotsFor={getViolationScreenshots}
                        onReview={(type, shots) => {
                          setSelectedViolationScreenshots({ type, screenshots: shots });
                          setShowProctoringDetails(true);
                        }}
                        answers={proctoringAnswers}
                        secondaryCamera={(() => {
                          // Only present when the org has a secondary camera
                          // configured; shown as one more check row, its
                          // captures behind See Details.
                          const cameraRound = secondaryCameraData?.rounds?.find(
                            (r) => r.round === 'interview' && r.summary,
                          );
                          if (!cameraRound) {
                            return secondaryCameraLoading ? { loading: true } : null;
                          }
                          const meta = cameraTileMeta(cameraRound);
                          const slides = buildCameraSlides(cameraRound);
                          return {
                            count: meta.count,
                            subline: meta.subline,
                            onOpen:
                              slides.length > 0
                                ? () => {
                                    setSelectedViolationScreenshots({
                                      type: 'secondary_camera_interview',
                                      screenshots: slides,
                                    });
                                    setShowProctoringDetails(true);
                                  }
                                : null,
                          };
                        })()}
                      />

                      {/* No Data Message. The violation grid above now renders
                          at zero as well, so this fires only when proctoring
                          produced nothing at all for this session. */}
                      {!evaluationData?.proctoring_report &&
                        !hasViolationData &&
                        (evaluationData?.violation_logs || []).length === 0 && (
                          <div className="flex flex-col items-center justify-center py-12">
                            <ChartPie className="w-16 h-16 text-gray-500 mb-4" />
                            <h3 className="text-[#A8A8A8] text-lg font-medium mb-2">
                              No Proctoring Data Available
                            </h3>
                            <p className="text-[#A8A8A8] text-sm text-center">
                              Proctoring data has not been generated for this assessment session.
                            </p>
                          </div>
                        )}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── CODING TAB ────────────────────────────────────────────────── */}
            {proctoringModalTab === 'coding' && (
              <>
                {selectedCodingViolationScreenshots && showCodingProctoringDetails ? (
                  <ProctoringScreenshotsView
                    violationType={selectedCodingViolationScreenshots.type}
                    screenshots={selectedCodingViolationScreenshots.screenshots}
                    onBack={() => {
                      setShowCodingProctoringDetails(false);
                      setSelectedCodingViolationScreenshots(null);
                    }}
                  />
                ) : (
                  <div className="px-6">
                    {codingViolationLoading ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <CircularProgress size={40} sx={{ color: '#C9A3FF' }} />
                        <p className="dark:text-[#A8A8A8] text-textSecondary text-sm mt-4">
                          Loading coding proctoring data…
                        </p>
                      </div>
                    ) : (() => {
                      const violationCounts = codingViolationData?.violation_counts;
                      const violationEntries = violationCounts
                        ? Object.entries(violationCounts).filter(
                            // The camera tile below already covers this count.
                            ([type, c]) => c > 0 && type !== 'secondary_camera_disconnect',
                          )
                        : [];
                      const cameraRound = secondaryCameraData?.rounds?.find(
                        (r) => r.round === 'coding' && r.summary,
                      );
                      const cameraStillLoading = secondaryCameraLoading && !cameraRound;
                      const hasData = violationEntries.length > 0 || cameraRound || cameraStillLoading;

                      return hasData ? (
                        <>
                          {/* Violation Detections */}
                          <div>
                            <div className="flex items-center mb-4">
                              <ShieldBan className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
                              <span className="dark:text-[#C9A3FF] text-textSecondary text-sm ml-2">
                                Violation Detections
                              </span>
                              <div className="flex-1 border dark:border-[#52525280] border-gray-200 ml-3"></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {violationEntries.map(([violationType, count]) => (
                                <ReportTile
                                  key={violationType}
                                  label={formatCodingViolationType(violationType)}
                                  count={count}
                                  seeDetails={codingViolationHasScreenshots(violationType)}
                                  onClick={() => {
                                    if (codingViolationHasScreenshots(violationType)) {
                                      setSelectedCodingViolationScreenshots({
                                        type: violationType,
                                        screenshots: getCodingViolationScreenshots(violationType),
                                      });
                                      setShowCodingProctoringDetails(true);
                                    }
                                  }}
                                />
                              ))}
                              {cameraStillLoading && <ReportTileSkeleton />}
                              {cameraRound &&
                                (() => {
                                  const meta = cameraTileMeta(cameraRound);
                                  return (
                                    <ReportTile
                                      label={formatCodingViolationType('secondary_camera_coding')}
                                      count={meta.count}
                                      countTone={meta.countTone}
                                      subline={meta.subline}
                                      seeDetails={meta.hasDetail}
                                      onClick={() => {
                                        if (meta.hasDetail) {
                                          setSelectedCodingViolationScreenshots({
                                            type: 'secondary_camera_coding',
                                            screenshots: buildCameraSlides(cameraRound),
                                          });
                                          setShowCodingProctoringDetails(true);
                                        }
                                      }}
                                    />
                                  );
                                })()}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12">
                          <ChartPie className="w-16 h-16 text-gray-500 mb-4" />
                          <h3 className="text-[#A8A8A8] text-lg font-medium mb-2">
                            No Coding Proctoring Violations
                          </h3>
                          <p className="text-[#A8A8A8] text-sm text-center">
                            No proctoring violations were detected during the coding round,
                            or the coding round has not been completed yet.
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            )}

          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Cancel Interview"
        message="Are you sure you want to cancel this interview? The candidate will no longer be able to start or continue the interview. This action cannot be undone."
        confirmText="Cancel Interview"
        cancelText="Go Back"
        onConfirm={handleDelete}
      />

      <Dialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `${isDarkMode === 'dark' ? 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))' : ''}`,
            backdropFilter: 'blur(50px)',
            WebkitBackdropFilter: 'blur(50px)',
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            transition: 'none !important',
          },
        }}
        TransitionProps={{
          timeout: 0,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography
            fontSize={18}
            fontWeight="400"
            mb={3}
            fontFamily="Poppins"
            // color="#D9D9D9"
            className="dark:text-[#D9D9D9] text-textPrimary"
          >
            Invite by email
          </Typography>
          <TextField
            autoFocus
            placeholder="email@example.com"
            type="email"
            fullWidth
            variant="outlined"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,

                // ----------- TEXT COLOR -----------
                '& .MuiInputBase-input': {
                  color: isDarkMode === 'dark' ? 'white' : '#1F2937', // dark: white, light: gray-800
                },

                // ----------- PLACEHOLDER COLOR -----------
                '& .MuiInputBase-input::placeholder': {
                  color: isDarkMode === 'dark' ? '#A9A9A9' : '#9CA3AF', // dark: gray-300, light: gray-400
                  opacity: 1,
                },
              },

              // ----------- OUTLINE BORDER -----------
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  isDarkMode === 'dark'
                    ? 'rgba(255, 255, 255, 0.5)' // dark border
                    : '#D1D5DB', // light theme: gray-300
              },

              // ----------- HOVER BORDER -----------
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode === 'dark' ? 'white' : '#9CA3AF', // light theme: gray-400 on hover
              },

              // ----------- FOCUS BORDER -----------
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: isDarkMode === 'dark' ? 'white' : '#9CA3AF', // keep neutral gray, no blue
                },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined
                    fontSize="small"
                    className="dark:text-white text-textSecondary"
                  />
                </InputAdornment>
              ),
            }}
          />

          <Typography
            fontSize={12}
            // color="#D9D9D9"
            display="block"
            mb={4}
            fontFamily="Poppins"
            className="dark:text-[#D9D9D9] text-textSecondary"
          >
            Shared link will expire in 7 days
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              onClick={handleShareDialogClose}
              disabled={sharing}
              variant="text"
              sx={{ color: `${theme === 'dark' ? '#D9D9D9' : '#525252'}` }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleShareReport}
              variant="contained"
              disabled={sharing}
              disableElevation
              sx={{
                px: 3,
                py: 1,
                borderRadius: '9999px', // Fully rounded (Tailwind's rounded-full)
                background: 'linear-gradient(to left, #FB54AE, #2F64FF)', // Gradient
                color: 'white', // Text color
                '&:hover': {
                  background: 'linear-gradient(to left, #FB54AE, #2F64FF)', // Maintain gradient on hover
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(to left, #FB54AE, #2F64FF)', // Maintain gradient when disabled
                  opacity: 0.6,
                  color: 'white',
                },
              }}
            >
              {sharing ? <CircularProgress size={20} /> : 'Share'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={videoOpen}
        onClose={() => {
          setVideoOpen(false);
          setSelectedVideoUrl('');
          setVideoDuration(null);
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        fullWidth
        maxWidth="md"
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: `${isDarkMode === 'dark' && 'rgba(24, 14, 34, 1)'}`,
            borderRadius: '12px',
            padding: '16px',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: `${isDarkMode === 'dark' ? '#C9A3FF' : '#525252'}`,
            fontFamily: 'Poppins',
          }}
        >
          {selectedVideoUrl === evaluationData?.introduction_video_url
            ? 'Introduction Video'
            : 'Video Response'}
          <button
            onClick={() => {
              setVideoOpen(false);
              setSelectedVideoUrl('');
              setVideoDuration(null);
              setIsPlaying(false);
              setCurrentTime(0);
              setNativeVideoFailed(false);
            }}
            className="absolute top-4 right-4 dark:text-white text-textSecondary"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </DialogTitle>
        <DialogContent>
          {selectedVideoUrl && nativeVideoFailed ? (
            // The <video> tag below failed to play this file (see
            // handleNativeVideoError) — most likely unfinalized WebM with no
            // Cues seek index. An iframe navigates to the URL the way typing
            // it into the address bar does, which plays these files fine —
            // at the cost of native browser player chrome instead of our
            // custom controls (cross-origin iframe can't be scripted).
            <iframe
              title="Video player"
              src={selectedVideoUrl}
              allow="autoplay"
              style={{ display: 'block', width: '100%', height: 480, border: 'none', borderRadius: '8px' }}
            />
          ) : selectedVideoUrl ? (
            <div style={{ position: 'relative' }}>
              {/* Hide ALL default controls */}
              <video
                ref={videoElementRef}
                key={selectedVideoUrl}
                src={selectedVideoUrl}
                width="100%"
                autoPlay
                preload="metadata"
                playsInline
                style={{
                  borderRadius: '8px',
                  backgroundColor: '#000',
                }}
                onLoadedMetadata={(e) => {
                  e.target.currentTime = 0;
                  // Update duration from video if backend didn't provide it
                  if (
                    !videoDuration &&
                    isFinite(e.target.duration) &&
                    e.target.duration > 0
                  ) {
                    setVideoDuration(e.target.duration);
                  }
                }}
                onDurationChange={(e) => {
                  // Fallback: set duration when it becomes available
                  if (
                    !videoDuration &&
                    isFinite(e.target.duration) &&
                    e.target.duration > 0
                  ) {
                    setVideoDuration(e.target.duration);
                  }
                }}
                onTimeUpdate={(e) => {
                  setCurrentTime(e.target.currentTime);
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onError={handleNativeVideoError}
              >
                Your browser does not support the video tag.
              </video>

              {/* Custom Controls - Only show when duration is available */}
              {videoDuration && isFinite(videoDuration) && videoDuration > 0 ? (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                    padding: '20px 16px 16px',
                  }}
                >
                  {/* Progress Bar */}
                  <input
                    type="range"
                    min="0"
                    max={videoDuration}
                    value={currentTime}
                    onChange={(e) => {
                      const newTime = parseFloat(e.target.value);
                      if (videoElementRef.current) {
                        videoElementRef.current.currentTime = newTime;
                      }
                      setCurrentTime(newTime);
                    }}
                    style={{
                      width: '100%',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      accentColor: '#C9A3FF',
                    }}
                  />

                  {/* Time Display and Controls */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      {/* Play/Pause */}
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            if (isPlaying) {
                              videoElementRef.current.pause();
                            } else {
                              videoElementRef.current.play();
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                        }}
                      >
                        {isPlaying ? (
                          <CirclePause className="w-8 h-8" />
                        ) : (
                          <CirclePlay className="w-8 h-8" />
                        )}
                      </button>

                      {/* Time */}
                      <span style={{ color: 'white', fontSize: '14px' }}>
                        {formatTime(currentTime)} / {formatTime(videoDuration)}
                      </span>
                    </div>

                    {/* Volume, Fullscreen, etc. */}
                    <div style={{ display: 'flex', gap: '11px' }}>
                      {/* Skip Back 10s */}
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            const video = videoElementRef.current;
                            // Ensure video is loaded and has a valid currentTime
                            if (video.readyState >= 2) {
                              const newTime = Math.max(
                                0,
                                video.currentTime - 10,
                              );
                              video.currentTime = newTime;
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                          opacity: 0.85,
                        }}
                        title="Back 10 seconds"
                      >
                        <SkipBack className="w-5 h-5" />
                      </button>
                      {/* Skip Forward 10s */}
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            const video = videoElementRef.current;
                            // Ensure video is loaded and has a valid currentTime
                            if (video.readyState >= 2) {
                              const newTime = video.currentTime + 10;
                              const maxTime =
                                videoDuration && isFinite(videoDuration)
                                  ? videoDuration
                                  : video.duration && isFinite(video.duration)
                                    ? video.duration
                                    : newTime;
                              video.currentTime = Math.min(maxTime, newTime);
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                          opacity: 0.85,
                        }}
                        title="Forward 10 seconds"
                      >
                        <SkipForward className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            if (videoElementRef.current.requestFullscreen) {
                              videoElementRef.current.requestFullscreen();
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Loading state - show minimal controls without progress bar */
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                    padding: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      {/* Play / Pause */}
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            isPlaying
                              ? videoElementRef.current.pause()
                              : videoElementRef.current.play();
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                        }}
                      >
                        {isPlaying ? (
                          <CirclePause className="w-8 h-8" />
                        ) : (
                          <CirclePlay className="w-8 h-8" />
                        )}
                      </button>
                    </div>

                    {/* Fullscreen */}
                    <div style={{ display: 'flex', gap: '11px' }}>
                      {/* Skip Back 10s */}
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            const video = videoElementRef.current;
                            // Ensure video is loaded and has a valid currentTime
                            if (video.readyState >= 2) {
                              const newTime = Math.max(
                                0,
                                video.currentTime - 10,
                              );
                              video.currentTime = newTime;
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                          opacity: 0.85,
                        }}
                        title="Back 10 seconds"
                      >
                        <SkipBack className="w-5 h-5" />
                      </button>
                      {/* Skip Forward 10s */}
                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            const video = videoElementRef.current;
                            // Ensure video is loaded and has a valid currentTime
                            if (video.readyState >= 2) {
                              const newTime = video.currentTime + 10;
                              const maxTime =
                                videoDuration && isFinite(videoDuration)
                                  ? videoDuration
                                  : video.duration && isFinite(video.duration)
                                    ? video.duration
                                    : newTime;
                              video.currentTime = Math.min(maxTime, newTime);
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                          opacity: 0.85,
                        }}
                        title="Forward 10 seconds"
                      >
                        <SkipForward className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => {
                          if (videoElementRef.current) {
                            if (videoElementRef.current.requestFullscreen) {
                              videoElementRef.current.requestFullscreen();
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'white',
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Skip Controls Overlay - Only show when duration is available */}
              {/* <div
                  style={{
                    position: "absolute",
                    bottom: "80px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "12px",
                    zIndex: 10,
                  }}
                >
                  <button
                    onClick={() => {
                      if (videoElementRef.current) {
                        const video = videoElementRef.current;
                        // Ensure video is loaded and has a valid currentTime
                        if (video.readyState >= 2) { 
                          const newTime = Math.max(0, video.currentTime - 10);
                          video.currentTime = newTime;
                        }
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white rounded-lg shadow-lg transition-all"
                  >
                    <SkipBack className="w-5 h-5" />
                    <span className="font-medium">10s</span>
                  </button>

                  <button
                    onClick={() => {
                      if (videoElementRef.current) {
                        const video = videoElementRef.current;
                        // Ensure video is loaded and has a valid currentTime
                        if (video.readyState >= 2) { 
                          const newTime = video.currentTime + 10;
                          const maxTime = (videoDuration && isFinite(videoDuration))
                            ? videoDuration
                            : (video.duration && isFinite(video.duration))
                              ? video.duration
                              : newTime;
                          video.currentTime = Math.min(maxTime, newTime);
                        }
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white rounded-lg shadow-lg transition-all"
                  >
                    <span className="font-medium">10s</span>
                    <SkipForward className="w-5 h-5" />
                  </button>
                </div> */}
            </div>
          ) : (
            <Typography sx={{ color: '#A8A8A8', fontFamily: 'Poppins' }}>
              No video available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <VideoPlayerModal
        open={videoLibraryOpen}
        onClose={() => setVideoLibraryOpen(false)}
        videoUrls={evaluationData?.video_urls || []}
        questions={evaluationData?.questions || []}
        durations={evaluationData?.duration || []}
        followUps={evaluationData?.follow_ups || []}
      />
      <VideoPlayerModal
        open={liveAvatarRecordingOpen}
        onClose={() => setLiveAvatarRecordingOpen(false)}
        videoUrls={
          evaluationData?.live_avatar_recording_url
            ? [evaluationData.live_avatar_recording_url]
            : []
        }
        questions={['Full Interview Recording']}
      />
      <LimitExceededModal
        isOpen={limitModal.isOpen}
        onClose={() =>
          setLimitModal({ isOpen: false, featureKey: null, errorCode: null })
        }
        featureKey={limitModal.featureKey}
        errorCode={limitModal.errorCode}
      />

      <AddRemarkModal
        isOpen={isAddRemarkModalOpen}
        onClose={() => setIsAddRemarkModalOpen(false)}
        onSubmit={(remark) => {
          dispatch(
            addInterviewCommentThunk({
              interviewId: interview_id,
              commentContent: remark,
              isAdmin: isAdminLoggedIn,
            })
          );
        }}
        candidateName={candidateData?.name}
        interviewId={interview_id}
      />

      {/* Analysis Report Modal */}
      <Dialog
        open={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        maxWidth="md"
        scroll="body"
        fullWidth
        PaperProps={{
          sx: {
            background:
              isDarkMode === 'dark'
                ? 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))'
                : '',
            backdropFilter: 'blur(50px)',
            WebkitBackdropFilter: 'blur(50px)',
            maxHeight: 'none',
            height: 'auto',
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center px-6 py-4">
          <span className="dark:text-[#8893C8] text-textPrimary text-sm font-medium">
            Analysis Report
          </span>

          <div className="flex items-center gap-3">
            {analysisReportData?.resume_s3_url ? (
              <button
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                onClick={handleViewResume}
              >
                <Download className="w-4 h-4 dark:text-[#C9A3FF] text-textSecondary" />
                <span className="dark:text-[#C9A3FF] text-textSecondary text-sm">
                  Download Resume
                </span>
              </button>
            ) : (
              <span className="text-gray-500 text-xs italic">No Resume</span>
            )}

            <IconButton
              onClick={() => setIsAnalysisModalOpen(false)}
              size="small"
              sx={{
                color: '#8893C8',
                '&:hover': { color: 'white' },
                padding: '4px',
              }}
            >
              <CloseIcon className="dark:text-white text-textSecondary" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent
          className="dark:text-white text-textPrimary"
          sx={{ maxHeight: 'none', overflow: 'visible' }}
        >
          {analysisReportData && Object.keys(analysisReportData).length > 0 ? (
            <div className="w-full px-4 sm:px-0 pt-5">
              <div className="rounded-3xl dark:shadow-xl shadow-sm p-4 sm:p-6 border border-gray-700/50">
                {/* SCORE + TOP DETAILS */}
                <div className="flex space-x-6">
                  <div className="h-36 w-36 relative">
                    <CircularProgressbar
                      value={
                        !analysisReportData.score ||
                        analysisReportData.score === 'NA' ||
                        analysisReportData.score === 'ERROR'
                          ? 0
                          : analysisReportData.score
                      }
                      text={
                        !analysisReportData.score ||
                        analysisReportData.score === 'NA' ||
                        analysisReportData.score === 'ERROR'
                          ? 'NA'
                          : `${analysisReportData.score}%`
                      }
                      circleRatio={0.75}
                      styles={buildStyles({
                        rotation: 1 / 2 + 1 / 8,
                        strokeLinecap: 'butt',
                        trailColor: getScoreColorGradient(
                          analysisReportData.score || 0,
                        ),
                        pathColor: getPathColor(analysisReportData.score || 0),
                        textColor:
                          isDarkMode === 'dark' ? '#FFFFFF' : '#525252',
                        textSize: '20px',
                      })}
                    />

                    <div
                      style={{
                        position: 'absolute',
                        width: '100%',
                        textAlign: 'center',
                        fontSize: '14px',
                        bottom: '10px',
                      }}
                      className="dark:text-white text-textSecondary"
                    >
                      Score
                    </div>
                  </div>

                  <div className="flex-1 flex-col space-y-6 pl-4">
                    {/* EXPERIENCE / LOCATION / EDUCATION */}
                    <div className="flex justify-between">
                      {/* Experience */}
                      <div className="flex-1 flex flex-col border-r border-[#484474]">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Experience Match
                        </span>

                        <div className="mt-2">
                          {Array.isArray(analysisReportData.experience_match) &&
                          analysisReportData.experience_match.length > 0 ? (
                            analysisReportData.experience_match.map(
                              (exp, index) => (
                                <span
                                  key={index}
                                  style={{
                                    color:
                                      exp.meets_requirement?.toLowerCase() ===
                                      'yes'
                                        ? '#34B53A'
                                        : '#DC0000',
                                  }}
                                >
                                  {exp.candidate_experience}
                                </span>
                              ),
                            )
                          ) : (
                            <span className="text-textSecondary">
                              No experience data available
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex-1 flex flex-col border-r border-[#484474] pl-5">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Location
                        </span>

                        <span className="mt-2">
                          {analysisReportData.location_match
                            ? analysisReportData.location_match.candidate_location?.split(
                                ',',
                              )[0]
                            : 'No location data available'}
                        </span>
                      </div>

                      {/* Education */}
                      <div className="flex-1 flex flex-col pl-5">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Education
                        </span>

                        <span className="mt-2">
                          {analysisReportData.education_match
                            ?.candidate_highest_education ||
                            'No education data available'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-5 border-t border-[#484474]" />

                    {/* RED FLAGS + TECH MATCH */}
                    <div className="flex justify-between">
                      <div className="flex flex-col border-r border-[#484474] w-[32%]">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Red Flags
                        </span>

                        <span className="mt-2">
                          {analysisReportData.red_flags?.length
                            ? analysisReportData.red_flags.join(', ')
                            : 'None'}
                        </span>
                      </div>

                      <div className="flex-1 flex flex-col pl-5">
                        <span className="dark:text-[#8893C8] text-textSecondary text-sm font-semibold">
                          Tech/Tools Match
                        </span>

                        <div className="mt-2 flex flex-wrap gap-3">
                          {analysisReportData.tech_tool_match?.map(
                            (skill, i) => (
                              <SkillBadge
                                key={i}
                                skill={skill.skill_name}
                                status={
                                  skill.skill_status?.toLowerCase() === 'yes'
                                    ? 'yes'
                                    : 'no'
                                }
                              />
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* HIGHLIGHTS / LOWLIGHTS / SUGGESTIONS */}
                <div className="mt-5 border-t border-[#484474]">
                  <div className="flex justify-between mt-5">
                    <div className="flex-1 border-r border-[#484474] pr-5">
                      <span className="dark:text-[#8893C8] text-sm font-semibold">
                        Highlights
                      </span>

                      <span className="mt-2 block">
                        {Array.isArray(strengths) && strengths.length
                          ? strengths
                              .map((p) => p.replace(/^- /, '').trim())
                              .join(' ')
                          : 'No highlights available'}
                      </span>
                    </div>

                    <div className="flex-1 pl-5">
                      <span className="dark:text-[#8893C8] text-sm font-semibold">
                        Lowlights
                      </span>

                      <span className="mt-2 block">
                        {Array.isArray(weaknesses) && weaknesses.length
                          ? weaknesses
                              .map((p) => p.replace(/^- /, '').trim())
                              .join(' ')
                          : 'No lowlights available'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 border-t border-[#484474] pt-5">
                    <span className="dark:text-[#8893C8] text-sm font-semibold">
                      Suggestions
                    </span>

                    <span className="mt-2 block">
                      {Array.isArray(suggestions) && suggestions.length
                        ? suggestions
                            .map((p) => p.replace(/^- /, '').trim())
                            .join(' ')
                        : 'No suggestions available'}
                    </span>
                  </div>

                  {/* ADDITIONAL INFO */}
                  {candidateData?.form_data &&
                    Object.keys(candidateData.form_data).length > 0 && (
                      <div className="mt-8 border-t border-[#484474] pt-6">
                        <h3 className="dark:text-[#8893C8] text-base font-semibold mb-5">
                          Additional Information
                        </h3>

                        {(() => {
                          const items = Object.entries(
                            candidateData.form_data || {},
                          ).filter(([_, value]) => value && value !== 'NA');

                          const itemsPerPage = 6;
                          const totalPages = Math.ceil(
                            items.length / itemsPerPage,
                          );
                          const start = additionalInfoPage * itemsPerPage;

                          const currentItems = items.slice(
                            start,
                            start + itemsPerPage,
                          );

                          return (
                            <div className="relative">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-8 px-12">
                                {currentItems.map(([key, value], i) => (
                                  <div
                                    key={i}
                                    className="px-6 py-4 border-r border-[#484474] last:border-r-0"
                                  >
                                    <div className="text-sm text-gray-400 mb-1">
                                      {key
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, (char) =>
                                          char.toUpperCase(),
                                        )}
                                    </div>

                                    <div className="dark:text-[#D0D5EF] text-textSecondary font-medium truncate overflow-hidden whitespace-nowrap">
                                      {value}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {totalPages > 1 && (
                                <>
                                  <button
                                    onClick={() =>
                                      setAdditionalInfoPage((p) =>
                                        Math.max(0, p - 1),
                                      )
                                    }
                                    className="absolute left-0 top-1/2 -translate-y-1/2"
                                  >
                                    <ChevronLeft className="w-6 h-6 text-indigo-400" />
                                  </button>

                                  <button
                                    onClick={() =>
                                      setAdditionalInfoPage((p) =>
                                        Math.min(totalPages - 1, p + 1),
                                      )
                                    }
                                    className="absolute right-0 top-1/2 -translate-y-1/2"
                                  >
                                    <ChevronRight className="w-6 h-6 text-indigo-400" />
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-textSecondary">No report available</p>
          )}

          <div className="flex justify-end gap-3 mt-6 px-2 pb-2">
            <button
              className="button text-sm"
              onClick={() => setIsAnalysisModalOpen(false)}
            >
              <span className="text-white">Close</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={showReasonModel}
        onClose={() => {
          setShowReasonModel(false);
          setSelectedCandidate(null);
        }}
        title="Tell Us Reason for Rejecting Candidate"
        message="Are you sure you want to Reject this candidate?"
        confirmText="Reject"
        cancelText="Cancel"
        takeReason={true}
        onConfirm={(reasonText) => {
          setShowReasonModel(false);
          handleupdateCandidateStatus(
            selectedCandidate,
            'REJECTED',
            reasonText,
          );
          setSelectedCandidate(null);
        }}
      />
    </>
  );
};

export default EvaluationReportOrgAdmin;